'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogContent,
    FormControlLabel,
    IconButton,
    LinearProgress,
    MenuItem,
    Select,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import MovieCreationOutlinedIcon from '@mui/icons-material/MovieCreationOutlined';
import GridViewOutlinedIcon from '@mui/icons-material/GridViewOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import StarIcon from '@mui/icons-material/Star';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import PlayCircleOutlinedIcon from '@mui/icons-material/PlayCircleOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import VideoLibraryOutlinedIcon from '@mui/icons-material/VideoLibraryOutlined';
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import ContentCutOutlinedIcon from '@mui/icons-material/ContentCutOutlined';
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
    clearCampaignWorkspace,
    getCampaignWorkspace,
    listCampaignThreadMessages,
    submitParticipantWork,
    submitLiveUrl,
    setParticipantUploadProgress,
    withdrawFromCampaign,
    requestDeliveryExtension,
} from '../../redux/buzzbridge/actions';
import { terminateUplaod } from '../../redux/upload/action';
import type {
    ICampaignWorkspaceCampaign,
    ICampaignWorkspaceData,
    IWorkspaceContentSubmission,
} from '../../redux/buzzbridge/campaignWorkspace.types';
import type { IDeliverableRound } from '../../redux/buzzbridge/types';
import {
    campaignWorkspaceErrorSelector,
    campaignWorkspaceSelector,
    gettingCampaignWorkspaceSelector,
    loadingOlderCampaignThreadMessagesSelector,
    participantWorkSubmitErrorSelector,
    submittingParticipantWorkSelector,
    submittingLiveUrlSelector,
    participantUploadProgressSelector,
    withdrawingFromCampaignSelector,
    requestingDeliveryExtensionSelector,
} from '../../redux/buzzbridge/selectors';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { ROUTES } from '../../types/global/routes.types';
import { dashboardHubPath } from '../../utils/dashboardHub';
import { replaceRouteParam } from '../../utils/routesProcessor';
import {
    buildCommissionShareUrl,
    buildFrontendReferralUrl,
    localizeCommissionShareUrl,
    normalizeApiCircuulShareUrl,
} from '../../utils/commissionShareUrl';
import { showToastMessage } from '../../utils/AppUtils';
import CustomLazyLoadImage from '../../components/Image/CustomLazyLoadImage';
import { RichTextViewer } from '../../components/Common/RichTextViewer';
import { ParticipantWorkspaceDiscussion } from '../BrandDashboard/campaignDetail/ParticipantWorkspaceDiscussion';
import { PublishToSocialModal } from '../../components/PublishToSocialModal/PublishToSocialModal';
import { isSocialPublishLocked } from '../../components/PublishToSocialModal/socialPublishLock';
import { usePlanEntitlementGates } from '../../hook/usePlanEntitlementGates';
import BrandReviewModal from '../../components/BuzzBridge/BrandReviewModal';
import { CampaignParticipationGateBanner } from '../../components/BuzzBridge/CampaignParticipationGateBanner';
import { CampaignCapacityInfoBanner } from '../../components/BuzzBridge/CampaignCapacityInfoBanner';
import { CampaignSyncUploadNotice } from '../../components/BuzzBridge/CampaignSyncUploadNotice';
import { CommissionActivityModal } from '../../components/BuzzBridge/CommissionActivityModal';
import { CreatorWorkspacePerformanceBoxes } from '../../components/BuzzBridge/CreatorWorkspacePerformanceBoxes';
import type { ICreatorBrandReviewContext } from '../../redux/buzzbridge/brandReview.types';
import {
    getParticipationGateActionLockMessage,
    isCreatorWorkspaceParticipationBlocked,
    resolveCampaignCapacity,
    resolveParticipationGate,
    shouldShowCampaignCapacityInfo,
} from '../../utils/campaignParticipationGate';
import { hasActiveCreatorThspianPlanSelector } from '../EsSettings/selector';
import { toggleSubscriptionModal } from '../../redux/appConfigurator/action';
import { DELIVERABLE_TYPE_LABELS } from '../../utils/deliverableLabels';
import { useCampaignThreadMarkReadOnOpen } from '../../hook/useCampaignThreadMarkReadOnOpen';
import { useCampaignThreadSocketRoom } from '../../hook/useCampaignThreadSocketRoom';

const BG = '#0e0e0e';
const SURFACE_LOW = '#131313';
const SURFACE = '#1a1919';
const SURFACE_HI = '#262626';
const GOLD = '#ffc965';
const MINT = '#00fdc1';
const ON_VARIANT = '#adaaaa';
const MAX_BYTES = 500 * 1024 * 1024;

type WorkspaceDeliverableDef = {
    type: string;
    label: string;
    count: number;
    duration_range: string;
    multiple_uploads?: { enabled: boolean; count: number; labels: string[] };
};

const FALLBACK_DELIVERABLE_TYPES: WorkspaceDeliverableDef[] = [
    { type: 'ugc_video', label: 'UGC Video', count: 1, duration_range: 'n/a' },
    { type: 'ugc_photo', label: 'UGC Photo', count: 1, duration_range: 'n/a' },
];

function iconForLabel(label: string | undefined): typeof MovieCreationOutlinedIcon {
    const l = (label ?? '').toLowerCase();
    if (l.includes('story') || l.includes('stories')) return HistoryOutlinedIcon;
    if (l.includes('grid') || l.includes('feed') || l.includes('post')) return GridViewOutlinedIcon;
    if (l.includes('tiktok')) return PlayCircleOutlinedIcon;
    return MovieCreationOutlinedIcon;
}

function rewardFromCampaign(c: ICampaignWorkspaceCampaign): string {
    const o = c as Record<string, unknown>;
    const raw = o.possible_earning ?? o.budget ?? o.reward;
    if (typeof raw === 'number' && !Number.isNaN(raw)) {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(raw);
    }
    if (typeof raw === 'string' && raw.trim()) {
        const n = parseFloat(raw.replace(/[^0-9.]/g, ''));
        if (!Number.isNaN(n)) {
            return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
        }
        return raw;
    }
    return '—';
}

function statusUi(status: string): { label: string; dot: string } {
    const s = String(status).toLowerCase();
    if (s === 'completed') return { label: 'Completed', dot: ON_VARIANT };
    if (s === 'pending') return { label: 'Pending', dot: GOLD };
    return { label: 'In Progress', dot: MINT };
}

function deadlineBlock(ws: ICampaignWorkspaceData): { dateLine: string; sub: string; pct: number } {
    // Prefer the participant's approved extension date over the campaign end date
    const end = (ws.participant as any).submission_delivery_date || ws.campaign.end_date;
    if (!end) return { dateLine: '—', sub: '', pct: 45 };
    const endD = new Date(end);
    const now = Date.now();
    const days = Math.ceil((endD.getTime() - now) / 86400000);
    const sub = days > 0 ? `${days} Day${days === 1 ? '' : 's'} Left` : 'Past due';
    const dateLine = endD.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const start = ws.campaign.start_date;
    let pct = 65;
    if (start) {
        const startT = new Date(start).getTime();
        const span = endD.getTime() - startT;
        if (span > 0) pct = Math.min(100, Math.max(5, ((now - startT) / span) * 100));
    } else {
        pct = days > 0 ? Math.min(95, Math.max(20, 100 - days * 7)) : 100;
    }
    return { dateLine, sub, pct };
}

function brandManager(ws: ICampaignWorkspaceData): { name: string; avatar?: string; subtitle: string } {
    const cb = ws.campaign.created_by;
    if (cb && typeof cb === 'object' && 'firstName' in cb) {
        const u = cb as { firstName?: string; lastName?: string; profile_picture?: string };
        const n = [u.firstName, u.lastName].filter(Boolean).join(' ');
        if (n) return { name: n, avatar: u.profile_picture, subtitle: 'Brand Manager' };
    }
    const b = ws.brand;
    return {
        name: b?.name ?? 'Brand',
        avatar: typeof b?.profile_picture === 'string' ? b.profile_picture : undefined,
        subtitle: 'Brand Manager',
    };
}

export function CreatorCampaignWorkspaceView({
    participantId,
    campaignId,
    currentUserId,
}: {
    participantId: string;
    campaignId: string;
    currentUserId?: string;
}) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const workspace = useSelector(campaignWorkspaceSelector);
    const loading = useSelector(gettingCampaignWorkspaceSelector);
    const loadingOlder = useSelector(loadingOlderCampaignThreadMessagesSelector);
    const error = useSelector(campaignWorkspaceErrorSelector);
    const submitting = useSelector(submittingParticipantWorkSelector);
    const uploadProgress = useSelector(participantUploadProgressSelector);
    const submitError = useSelector(participantWorkSubmitErrorSelector);
    const submittingLiveUrl = useSelector(submittingLiveUrlSelector);
    const withdrawing = useSelector(withdrawingFromCampaignSelector);
    const requestingExtension = useSelector(requestingDeliveryExtensionSelector);
    const isSubscribed = useSelector(hasActiveCreatorThspianPlanSelector);

    const [optOutDialogOpen, setOptOutDialogOpen] = useState(false);
    const [extensionDialogOpen, setExtensionDialogOpen] = useState(false);
    const [extensionDays, setExtensionDays] = useState('');
    const [extensionReason, setExtensionReason] = useState('');
    const [extensionRoundId, setExtensionRoundId] = useState<string | undefined>(undefined);
    const [uploadFailed, setUploadFailed] = useState(false);
    const prevUploadProgress = useRef(0);

    useEffect(() => {
        const prev = prevUploadProgress.current;
        prevUploadProgress.current = uploadProgress;
        // Progress reset to 0 while we were mid-upload → something failed
        if (prev > 0 && prev < 100 && uploadProgress === 0) {
            setUploadFailed(true);
        }
        // Successful completion — clear any stale failure state
        if (uploadProgress === 100) {
            setUploadFailed(false);
        }
    }, [uploadProgress]);

    const handleCancelUpload = () => {
        dispatch(terminateUplaod());
        dispatch(setParticipantUploadProgress(0));
        setUploadFailed(false);
    };

    const [detailsOpen, setDetailsOpen] = useState(false);
    const [brandReviewOpen, setBrandReviewOpen] = useState(false);
    const [deliverableType, setDeliverableType] = useState('');
    const [slotIndex, setSlotIndex] = useState(0);
    const [files, setFiles] = useState<File[]>([]);
    const [localUploadError, setLocalUploadError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [activeLiveUrlType, setActiveLiveUrlType] = useState('');
    const [liveUrlInputs, setLiveUrlInputs] = useState<string[]>([]);
    const [pinnedRoundId, setPinnedRoundId] = useState<string | null>(null);
    const [graceLiveType, setGraceLiveType] = useState('');
    const [graceLiveInputs, setGraceLiveInputs] = useState<string[]>([]);
    const theme = useTheme();
    const isBottomSheetChat = useMediaQuery(theme.breakpoints.down('lg'));
    const [mobileChatExpanded, setMobileChatExpanded] = useState(false);

    // For recurrent campaigns: find the active round (earliest round with a future delivery date,
    // or the last round if all have passed)
    const activeRound = useMemo((): IDeliverableRound | null => {
        const rounds = (workspace?.campaign as any)?.deliverable_rounds as IDeliverableRound[] | undefined;
        if (!rounds?.length || !(workspace?.campaign as any)?.recurrent_deliverables) return null;
        if (pinnedRoundId) {
            const pinned = rounds.find(r => String(r._id) === pinnedRoundId);
            if (pinned) return pinned;
        }
        const now = Date.now();
        const future = rounds.filter(r => r.delivery_date && new Date(r.delivery_date).getTime() > now);
        if (future.length > 0) {
            return future.reduce((a, b) =>
                new Date(a.delivery_date!).getTime() < new Date(b.delivery_date!).getTime() ? a : b,
            );
        }
        // All rounds past — show last one
        return rounds[rounds.length - 1];
    }, [workspace, pinnedRoundId]);

    const campaignDeliverables = useMemo((): WorkspaceDeliverableDef[] => {
        if (!workspace) return [];
        // For recurrent campaigns, use the active round's deliverables
        const roundDeliverables = activeRound?.deliverables;
        const raw = roundDeliverables?.length ? roundDeliverables : workspace.campaign.deliverables ?? [];
        if (raw.length === 0) return FALLBACK_DELIVERABLE_TYPES;
        return (raw as any[])
            .filter((d: any) => d.type)
            .map((d: any) => ({
                type: d.type,
                label: DELIVERABLE_TYPE_LABELS[d.type] ?? d.type,
                count: d.count ?? 1,
                duration_range: d.duration_range ?? 'n/a',
                multiple_uploads: d.multiple_uploads ?? undefined,
            }));
    }, [workspace, activeRound]);

    // Commission campaigns can optionally require content deliverables — check if any are explicitly set
    const commissionHasDeliverables =
        workspace?.campaign.campaign_type === 'commission' &&
        ((activeRound?.deliverables?.length ?? 0) > 0 || (workspace?.campaign.deliverables?.length ?? 0) > 0);

    // App-install campaigns can also optionally require content deliverables alongside the referral link
    const appInstallHasDeliverables =
        !!workspace?.campaign.app_install?.enabled &&
        ((activeRound?.deliverables?.length ?? 0) > 0 || (workspace?.campaign.deliverables?.length ?? 0) > 0);

    const totalRequired = campaignDeliverables.length;
    const requiredCount = useMemo(
        () => campaignDeliverables.find(d => d.type === deliverableType)?.count ?? 1,
        [campaignDeliverables, deliverableType],
    );
    const activeDeliverableMultiUpload = useMemo(() => {
        const d = campaignDeliverables.find(del => del.type === deliverableType);
        return d?.multiple_uploads?.enabled ? d.multiple_uploads : null;
    }, [campaignDeliverables, deliverableType]);

    // Locked deliverables — submitted or approved, cannot re-upload
    // For recurrent campaigns, scope to the active round.
    // For multi-upload deliverables, only mark the type as done once ALL slots are submitted.
    const submittedTypes = useMemo(() => {
        const allSubs = workspace?.participant.content_submissions ?? [];
        const subs = activeRound?._id
            ? allSubs.filter(
                  s =>
                      (s as any).deliverable_round_id === String(activeRound._id) ||
                      (s as any).round_number === activeRound.round_number,
              )
            : allSubs;
        const doneSubs = subs.filter(s => s.status === 'submitted' || s.status === 'approved' || s.status === 'shared');
        // Count distinct submitted slots per deliverable type
        const slotsByType = new Map<string, Set<number>>();
        doneSubs.forEach(s => {
            if (!s.deliverable_type) return;
            if (!slotsByType.has(s.deliverable_type)) slotsByType.set(s.deliverable_type, new Set());
            slotsByType.get(s.deliverable_type)!.add((s as any).slot_index ?? 0);
        });
        const result = new Set<string>();
        slotsByType.forEach((slots, type) => {
            const deliverable = campaignDeliverables.find(d => d.type === type);
            const requiredSlots = deliverable?.multiple_uploads?.enabled ? deliverable.multiple_uploads.count ?? 1 : 1;
            if (slots.size >= requiredSlots) result.add(type);
        });
        return result;
    }, [workspace, activeRound, campaignDeliverables]);

    // Submitted slots per deliverable type — used to mark individual slots as done in multi-upload UI
    const submittedSlotsByType = useMemo(() => {
        const allSubs = workspace?.participant.content_submissions ?? [];
        const subs = activeRound?._id
            ? allSubs.filter(
                  s =>
                      (s as any).deliverable_round_id === String(activeRound._id) ||
                      (s as any).round_number === activeRound.round_number,
              )
            : allSubs;
        const map = new Map<string, Set<number>>();
        subs.filter(
            s => (s.status === 'submitted' || s.status === 'approved' || s.status === 'shared') && s.deliverable_type,
        ).forEach(s => {
            if (!map.has(s.deliverable_type!)) map.set(s.deliverable_type!, new Set());
            map.get(s.deliverable_type!)!.add((s as any).slot_index ?? 0);
        });
        return map;
    }, [workspace, activeRound]);

    // Deliverables that need revision — label → feedback message
    const revisionTypes = useMemo(() => {
        const allSubs = workspace?.participant.content_submissions ?? [];
        const subs = activeRound?._id
            ? allSubs.filter(
                  s =>
                      (s as any).deliverable_round_id === String(activeRound._id) ||
                      (s as any).round_number === activeRound.round_number,
              )
            : allSubs;
        const map = new Map<string, string>();
        subs.filter(s => s.status === 'needs_revision' && s.deliverable_type).forEach(s => {
            map.set(s.deliverable_type!, s.feedback ?? '');
        });
        return map;
    }, [workspace, activeRound]);

    // Approved submissions — deliverableType → submission (for live URL flow)
    const approvedSubmissions = useMemo(() => {
        const allSubs = workspace?.participant.content_submissions ?? [];
        const subs = activeRound?._id
            ? allSubs.filter(
                  s =>
                      (s as any).deliverable_round_id === String(activeRound._id) ||
                      (s as any).round_number === activeRound.round_number,
              )
            : allSubs;
        const map = new Map<string, IWorkspaceContentSubmission>();
        subs.filter(s => s.status === 'approved' && s.deliverable_type).forEach(s => {
            map.set(s.deliverable_type!, s);
        });
        return map;
    }, [workspace, activeRound]);

    const openLiveLinkWindows = useMemo(() => {
        const now = Date.now();
        const windows =
            (workspace?.participant as { live_link_windows?: { round_id?: string | null; expires_at?: string }[] })
                ?.live_link_windows || [];
        return windows.filter(w => w.expires_at && new Date(w.expires_at).getTime() > now);
    }, [workspace]);

    const activeWindowNote = useMemo(() => {
        const match = openLiveLinkWindows.find(w =>
            activeRound?._id ? String(w.round_id) === String(activeRound._id) : !w.round_id,
        );
        return match?.expires_at
            ? `The brand opened this form until ${formatLiveLinkWindow(match.expires_at)}.`
            : undefined;
    }, [openLiveLinkWindows, activeRound]);

    const graceRounds = useMemo(() => {
        const rounds = ((workspace?.campaign as { deliverable_rounds?: IDeliverableRound[] })?.deliverable_rounds ||
            []) as IDeliverableRound[];
        return openLiveLinkWindows.flatMap(w => {
            if (!w.round_id || !w.expires_at) return [];
            if (activeRound && String(w.round_id) === String(activeRound._id)) return [];
            const round = rounds.find(r => String(r._id) === String(w.round_id));
            return round ? [{ round, expiresAt: w.expires_at }] : [];
        });
    }, [openLiveLinkWindows, workspace, activeRound]);

    const selectedGrace = graceRounds[0] || null;
    const graceApproved = useMemo(() => {
        if (!selectedGrace || !workspace) return new Map<string, IWorkspaceContentSubmission>();
        return approvedSubmissionsForRound(workspace.participant.content_submissions ?? [], selectedGrace.round);
    }, [selectedGrace, workspace]);

    // Revision submission IDs — label → existing submission _id (for PATCH re-upload)
    const revisionSubmissionIds = useMemo(() => {
        const allSubs = workspace?.participant.content_submissions ?? [];
        const subs = activeRound?._id
            ? allSubs.filter(
                  s =>
                      (s as any).deliverable_round_id === String(activeRound._id) ||
                      (s as any).round_number === activeRound.round_number,
              )
            : allSubs;
        const map = new Map<string, string>();
        subs.filter(s => s.status === 'needs_revision' && s.deliverable_type && s._id).forEach(s => {
            map.set(s.deliverable_type!, s._id!);
        });
        return map;
    }, [workspace, activeRound]);

    const totalSubmitted = submittedTypes.size;

    const pid = participantId?.trim() || '';

    useCampaignThreadMarkReadOnOpen({
        participantId: pid,
        campaignId: campaignId || workspace?.campaign._id,
        perspective: 'creator',
        enabled: Boolean(workspace && !loading),
    });

    useCampaignThreadSocketRoom(pid, Boolean(pid && workspace && !loading));

    const participationGate = useMemo(() => resolveParticipationGate(workspace ?? undefined), [workspace]);
    const participationBlocked = useMemo(
        () => isCreatorWorkspaceParticipationBlocked(workspace ?? undefined),
        [workspace],
    );
    const campaignCapacity = useMemo(() => resolveCampaignCapacity(workspace ?? undefined), [workspace]);
    const showCapacityInfo = useMemo(() => shouldShowCampaignCapacityInfo(workspace ?? undefined), [workspace]);
    const participationLockMessage = useMemo(
        () => getParticipationGateActionLockMessage(participationGate, 'upload'),
        [participationGate],
    );
    const liveUrlLockMessage = useMemo(
        () => getParticipationGateActionLockMessage(participationGate, 'live_url'),
        [participationGate],
    );
    const windowUnlocksEnded =
        participationGate?.blocked_reason_code === 'campaign_ended' && openLiveLinkWindows.length > 0;
    const deliveryActionBlocked = participationBlocked && !windowUnlocksEnded;

    const promptCreatorSubscribe = useCallback(() => {
        dispatch(toggleSubscriptionModal(true));
    }, [dispatch]);

    const ensureSubscriptionForWindow = useCallback(
        (roundId?: string | null) => {
            const covered = openLiveLinkWindows.some(w =>
                roundId ? String(w.round_id) === String(roundId) : !w.round_id,
            );
            if (!covered || isSubscribed) return true;
            promptCreatorSubscribe();
            return false;
        },
        [openLiveLinkWindows, isSubscribed, promptCreatorSubscribe],
    );

    useEffect(() => {
        if (!pinnedRoundId) return;
        const stillOpen = openLiveLinkWindows.some(w => String(w.round_id) === pinnedRoundId);
        if (!stillOpen) setPinnedRoundId(null);
    }, [pinnedRoundId, openLiveLinkWindows]);

    useEffect(() => {
        if (!isBottomSheetChat) setMobileChatExpanded(false);
    }, [isBottomSheetChat]);

    useEffect(() => {
        if (!isBottomSheetChat || !mobileChatExpanded) return undefined;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = prev;
        };
    }, [mobileChatExpanded, isBottomSheetChat]);

    // Auto-select: prefer first needs_revision, then first not-yet-submitted
    useEffect(() => {
        if (campaignDeliverables.length === 0) return;
        const firstRevision = campaignDeliverables.find(d => revisionTypes.has(d.type));
        const firstPending = campaignDeliverables.find(d => !submittedTypes.has(d.type));
        const target = firstRevision ?? firstPending;
        if (!target) return;
        const currentIsValid = campaignDeliverables.some(d => d.type === deliverableType);
        if (!currentIsValid || submittedTypes.has(deliverableType)) {
            setDeliverableType(target.type);
            setFiles([]);
        }
    }, [campaignDeliverables, submittedTypes, revisionTypes, deliverableType]);

    // Auto-advance slotIndex to the first pending slot for multi-upload deliverables
    useEffect(() => {
        const multiUploadDef = campaignDeliverables.find(d => d.type === deliverableType)?.multiple_uploads;
        if (!multiUploadDef?.enabled) return;
        const slots = submittedSlotsByType.get(deliverableType);
        for (let i = 0; i < (multiUploadDef.count ?? 1); i++) {
            if (!slots?.has(i)) {
                setSlotIndex(i);
                return;
            }
        }
    }, [deliverableType, submittedSlotsByType, campaignDeliverables]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!pid) return;
        dispatch(clearCampaignWorkspace());
        dispatch(
            getCampaignWorkspace({
                perspective: 'creator',
                participantId: pid,
                messagesPage: 1,
                messagesLimit: 20,
                appendMessages: false,
            }),
        );
        return () => {
            dispatch(clearCampaignWorkspace());
        };
    }, [dispatch, pid, currentUserId]);

    const loadOlder = useCallback(() => {
        if (!pid || !workspace?.messages_pagination) return;
        const { page, has_more_older } = workspace.messages_pagination;
        if (!has_more_older) return;
        dispatch(
            listCampaignThreadMessages({
                participantId: pid,
                messagesPage: page + 1,
                messagesLimit: workspace.messages_pagination.limit || 20,
                append: true,
            }),
        );
    }, [dispatch, pid, workspace]);

    const submitPath = useMemo(() => {
        if (!campaignId || !pid) return '';
        let p = replaceRouteParam(ROUTES.BUZZ_BRIDGE_CAMPAIGN_SUBMIT, 'campaignId', campaignId);
        p = replaceRouteParam(p, 'participantId', pid);
        return p;
    }, [campaignId, pid]);

    const creatorName =
        workspace?.creator.influencer?.name?.trim() ||
        (workspace?.creator.user && 'userName' in workspace.creator.user ? workspace.creator.user.userName : null) ||
        'there';

    const brandCounterpartName = workspace ? brandManager(workspace).name : 'Brand';

    const brandCounterpartIds = useMemo(() => {
        const ids: string[] = [];
        const createdBy = workspace?.campaign?.created_by;
        if (createdBy && typeof createdBy === 'object' && '_id' in createdBy && createdBy._id) {
            ids.push(String(createdBy._id));
        }
        return ids;
    }, [workspace]);

    // Auto-select first approved deliverable for live URL panel
    useEffect(() => {
        const firstType = approvedSubmissions.keys().next().value;
        if (firstType && !activeLiveUrlType) {
            setActiveLiveUrlType(firstType);
        }
    }, [approvedSubmissions, activeLiveUrlType]);

    const handleSubmitLiveUrl = useCallback(() => {
        if (deliveryActionBlocked) return;
        if (!ensureSubscriptionForWindow(activeRound?._id || null)) return;
        if (!pid || !activeLiveUrlType) return;
        const sub = approvedSubmissions.get(activeLiveUrlType);
        if (!sub?._id) return;
        const urls = liveUrlInputs.map(u => u.trim()).filter(u => u.startsWith('http'));
        if (urls.length === 0) return;
        dispatch(submitLiveUrl({ participantId: pid, submissionId: sub._id, liveUrls: urls }));
        setLiveUrlInputs([]);
    }, [
        pid,
        activeLiveUrlType,
        liveUrlInputs,
        approvedSubmissions,
        dispatch,
        deliveryActionBlocked,
        ensureSubscriptionForWindow,
        activeRound,
    ]);

    useEffect(() => {
        const firstType = graceApproved.keys().next().value;
        if (firstType && !graceApproved.has(graceLiveType)) setGraceLiveType(firstType);
    }, [graceApproved, graceLiveType]);

    const handleSubmitGraceLiveUrl = useCallback(() => {
        if (deliveryActionBlocked || !pid || !graceLiveType) return;
        if (!ensureSubscriptionForWindow(selectedGrace?.round._id || null)) return;
        const sub = graceApproved.get(graceLiveType);
        if (!sub?._id) return;
        const urls = graceLiveInputs.map(u => u.trim()).filter(u => u.startsWith('http'));
        if (urls.length === 0) return;
        dispatch(submitLiveUrl({ participantId: pid, submissionId: sub._id, liveUrls: urls }));
        setGraceLiveInputs([]);
    }, [
        deliveryActionBlocked,
        pid,
        graceLiveType,
        graceApproved,
        graceLiveInputs,
        dispatch,
        ensureSubscriptionForWindow,
        selectedGrace,
    ]);

    const handleRetry = useCallback(() => {
        if (!pid) return;
        dispatch(
            getCampaignWorkspace({
                perspective: 'creator',
                participantId: pid,
                messagesPage: 1,
                messagesLimit: 20,
                appendMessages: false,
            }),
        );
    }, [dispatch, pid]);

    const onFilesPicked = (list: FileList | null) => {
        if (!list?.length) return;
        const next: File[] = [];
        let big = false;
        let invalid = false;
        Array.from(list).forEach(f => {
            if (!f.type.startsWith('video/')) {
                invalid = true;
                return;
            }
            if (f.size > MAX_BYTES) {
                big = true;
                return;
            }
            next.push(f);
        });
        if (invalid) setLocalUploadError('Only video files are accepted.');
        else if (big) setLocalUploadError('Some files exceeded 500MB and were skipped.');
        else setLocalUploadError(null);
        setFiles(prev => [...prev, ...next]);
    };

    const handleSubmitForApproval = (selectedPlatforms: string[]) => {
        if (deliveryActionBlocked) return;
        if (!ensureSubscriptionForWindow(activeRound?._id || null)) return;
        setLocalUploadError(null);
        if (!pid || !campaignId) {
            setLocalUploadError('Missing campaign or participant.');
            return;
        }
        if (files.length === 0) {
            setLocalUploadError('Add at least one file before submitting.');
            return;
        }
        if (files.length !== requiredCount) {
            setLocalUploadError(
                `This deliverable requires exactly ${requiredCount} file${requiredCount > 1 ? 's' : ''}. You have ${
                    files.length
                }.`,
            );
            return;
        }
        dispatch(
            submitParticipantWork({
                participantId: pid,
                campaignId,
                deliverableType,
                description: '',
                files,
                submissionId: revisionSubmissionIds.get(deliverableType),
                navigateAfter: false,
                platforms: selectedPlatforms.length > 0 ? selectedPlatforms : undefined,
                ...(activeRound?._id && { deliverableRoundId: String(activeRound._id) }),
                ...(activeDeliverableMultiUpload ? { slotIndex } : {}),
            }),
        );
        setFiles([]);
    };

    const se = submitError as { data?: { message?: string }; message?: string } | undefined;
    const panelErr = localUploadError || (se ? se.data?.message || se.message || null : null);

    return (
        <Box sx={{ bgcolor: BG, color: '#fff', minHeight: '100vh', pb: 10, width: '100%', overflowX: 'hidden' }}>
            <Box
                component="header"
                sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 50,
                    bgcolor: BG,
                    borderBottom: '1px solid rgba(255,255,255,0.05)',
                    px: { xs: 2, sm: 3 },
                    py: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
                    <IconButton
                        onClick={() => navigate(dashboardHubPath())}
                        aria-label="Back"
                        sx={{ color: GOLD, p: 0.5 }}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontWeight: 800,
                            fontSize: 18,
                            color: GOLD,
                            letterSpacing: '-0.02em',
                        }}>
                        Campaign Workspace
                    </Typography>
                </Box>
                {workspace ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
                        {totalSubmitted === 0 && (
                            <Button
                                variant="contained"
                                size="small"
                                disabled={withdrawing}
                                onClick={() => setOptOutDialogOpen(true)}
                                sx={{
                                    bgcolor: '#c62828',
                                    color: '#fff',
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontSize: 12,
                                    px: 1.5,
                                    py: 0.75,
                                    borderRadius: 1.5,
                                    textTransform: 'none',
                                    '&:hover': { bgcolor: '#b71c1c' },
                                    '&:disabled': { bgcolor: 'rgba(198,40,40,0.4)', color: 'rgba(255,255,255,0.5)' },
                                }}>
                                {withdrawing ? <CircularProgress size={14} sx={{ color: '#fff', mr: 0.5 }} /> : null}
                                Opt out
                            </Button>
                        )}
                        <Typography
                            sx={{
                                display: { xs: 'none', sm: 'block' },
                                fontSize: 13,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                color: GOLD,
                            }}
                            noWrap>
                            {workspace.brand?.name ?? workspace.campaign.title}
                        </Typography>
                        <Box
                            sx={{
                                width: 40,
                                height: 40,
                                borderRadius: '50%',
                                overflow: 'hidden',
                                border: '1px solid rgba(255,201,101,0.2)',
                                bgcolor: SURFACE_HI,
                            }}>
                            {workspace.brand?.profile_picture ? (
                                <CustomLazyLoadImage
                                    src={workspace.brand.profile_picture}
                                    alt=""
                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                            ) : workspace.campaign.feature_image ? (
                                <CustomLazyLoadImage
                                    src={workspace.campaign.feature_image}
                                    alt=""
                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                            ) : null}
                        </Box>
                    </Box>
                ) : null}
            </Box>

            <Box
                sx={{
                    maxWidth: 1400,
                    mx: 'auto',
                    px: { xs: 2, md: 4 },
                    mt: 2,
                    pb: {
                        xs: workspace ? 'calc(96px + env(safe-area-inset-bottom, 0px))' : 0,
                        lg: 0,
                    },
                }}>
                {error && !workspace ? (
                    <Alert
                        severity="error"
                        action={
                            <Button color="inherit" size="small" onClick={handleRetry}>
                                Retry
                            </Button>
                        }
                        sx={{ mb: 2 }}>
                        Could not load campaign workspace.
                    </Alert>
                ) : null}

                {loading && !workspace ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                        <CircularProgress sx={{ color: GOLD }} />
                    </Box>
                ) : null}

                {!loading && !workspace && !error && !pid ? (
                    <Typography sx={{ color: ON_VARIANT }}>Missing participant.</Typography>
                ) : null}

                {workspace ? (
                    <>
                        {workspace.campaign.is_clipping && workspace.campaign.clipping_asset_url?.trim() ? (
                            <ClippingSourceBanner url={workspace.campaign.clipping_asset_url.trim()} />
                        ) : null}

                        <TopJobBanner
                            workspace={workspace}
                            activeRound={activeRound}
                            onOpenDetails={() => setDetailsOpen(true)}
                            onRequestExtension={() => {
                                setExtensionRoundId(undefined);
                                setExtensionDays('');
                                setExtensionReason('');
                                setExtensionDialogOpen(true);
                            }}
                        />

                        {participationGate && participationBlocked ? (
                            <CampaignParticipationGateBanner gate={participationGate} variant="creator" />
                        ) : null}

                        {showCapacityInfo ? <CampaignCapacityInfoBanner capacity={campaignCapacity} /> : null}

                        <BrandReviewPrompt workspace={workspace} onOpenReview={() => setBrandReviewOpen(true)} />

                        {/* Sample Videos */}
                        {(workspace.campaign.sample_videos ?? []).length > 0 && (
                            <Box sx={{ mb: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                    <VideoLibraryOutlinedIcon sx={{ fontSize: 14, color: GOLD }} />
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            fontSize: 13,
                                            color: GOLD,
                                            letterSpacing: '0.12em',
                                            textTransform: 'uppercase',
                                        }}>
                                        Sample Videos
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                    {workspace.campaign.sample_videos!.map((video, i) => (
                                        <Box
                                            key={video._id ?? i}
                                            onClick={() => window.open(video.url, '_blank')}
                                            sx={{
                                                position: 'relative',
                                                bgcolor: '#000',
                                                borderRadius: 2,
                                                border: '1px solid rgba(255,255,255,0.07)',
                                                cursor: 'pointer',
                                                width: { xs: '100%', sm: 'calc(50% - 8px)' },
                                                aspectRatio: '16/9',
                                                overflow: 'hidden',
                                                '&:hover .play-overlay': { opacity: 1 },
                                            }}>
                                            <Box
                                                component="video"
                                                src={video.url}
                                                preload="metadata"
                                                sx={{
                                                    width: '100%',
                                                    height: '100%',
                                                    display: 'block',
                                                    objectFit: 'cover',
                                                    pointerEvents: 'none',
                                                }}
                                            />
                                            <Box
                                                className="play-overlay"
                                                sx={{
                                                    position: 'absolute',
                                                    inset: 0,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    bgcolor: 'rgba(0,0,0,0.4)',
                                                    opacity: 0.85,
                                                    transition: 'opacity 0.2s',
                                                }}>
                                                <PlayCircleIcon sx={{ fontSize: 48, color: '#fff', opacity: 0.9 }} />
                                            </Box>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        )}

                        {/* Campaign Assets */}
                        {(workspace.campaign.assets ?? []).length > 0 && (
                            <Box sx={{ mb: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                    <FolderOpenOutlinedIcon sx={{ fontSize: 14, color: GOLD }} />
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            fontSize: 13,
                                            color: GOLD,
                                            letterSpacing: '0.12em',
                                            textTransform: 'uppercase',
                                        }}>
                                        Campaign Assets
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    {workspace.campaign.assets!.map((asset, i) => {
                                        const label = (() => {
                                            if (asset.description?.trim()) return asset.description.trim();
                                            try {
                                                const name = decodeURIComponent(
                                                    new URL(asset.url).pathname.split('/').pop() || '',
                                                );
                                                return name || 'Campaign asset';
                                            } catch {
                                                return 'Campaign asset';
                                            }
                                        })();
                                        return (
                                            <Box
                                                key={i}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    gap: 2,
                                                    p: 2,
                                                    bgcolor: SURFACE_LOW,
                                                    borderRadius: 2,
                                                    border: '1px solid rgba(255,255,255,0.06)',
                                                }}>
                                                <Typography
                                                    sx={{
                                                        fontSize: 13,
                                                        color: '#fff',
                                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                                        minWidth: 0,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                    }}>
                                                    {label}
                                                </Typography>
                                                <Button
                                                    component="a"
                                                    href={asset.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    download
                                                    size="small"
                                                    startIcon={<DownloadOutlinedIcon />}
                                                    sx={{
                                                        flexShrink: 0,
                                                        textTransform: 'none',
                                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                                        fontSize: 12,
                                                        color: GOLD,
                                                        borderColor: `${GOLD}66`,
                                                        border: '1px solid',
                                                        borderRadius: 1.5,
                                                        px: 1.5,
                                                        '&:hover': { borderColor: GOLD, bgcolor: `${GOLD}12` },
                                                    }}>
                                                    Download
                                                </Button>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            </Box>
                        )}

                        {workspace.campaign.campaign_type === 'direct_hire' && (
                            <Box
                                sx={{
                                    mb: 3,
                                    p: 2,
                                    borderRadius: 1,
                                    bgcolor: 'rgba(255,193,7,0.08)',
                                    border: '1px solid #ffc96544',
                                }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <HandshakeOutlinedIcon sx={{ fontSize: 13, color: '#ffc965' }} />
                                    <Typography
                                        sx={{
                                            fontSize: 10,
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.12em',
                                            color: '#ffc965',
                                        }}>
                                        Direct hire
                                    </Typography>
                                </Box>
                                <Typography
                                    sx={{ fontSize: 13, color: 'rgba(255,255,255,0.75)', lineHeight: 1.6, mb: 0.5 }}>
                                    You don't need to publish to social for this campaign.
                                </Typography>
                                <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', lineHeight: 1.55 }}>
                                    Submit your content for review and the brand will handle distribution. Payout is
                                    issued after your submission is approved.
                                </Typography>
                            </Box>
                        )}

                        {workspace.campaign.campaign_type === 'sync_upload' && workspace.campaign.sync_publish_date && (
                            <CampaignSyncUploadNotice
                                syncPublishDate={workspace.campaign.sync_publish_date}
                                isSignedIn
                                isAutoLinked={(() => {
                                    const inf = workspace.creator?.influencer as
                                        | {
                                              instagram?: { linked?: boolean };
                                              tiktok?: { linked?: boolean };
                                              youtube?: { linked?: boolean };
                                          }
                                        | undefined;
                                    const required = (workspace.campaign.platforms || []).filter(p =>
                                        ['instagram', 'tiktok', 'youtube'].includes(String(p)),
                                    );
                                    const platforms = required.length ? required : ['instagram'];
                                    return platforms.every(p => {
                                        const key = String(p) as 'instagram' | 'tiktok' | 'youtube';
                                        return Boolean(inf?.[key]?.linked);
                                    });
                                })()}
                                onConnectAccounts={() => {
                                    showToastMessage(
                                        'Connect your social accounts with OAuth from your creator profile.',
                                        'info',
                                    );
                                    navigate(ROUTES.SETTINGS);
                                }}
                                sx={{ mb: 3 }}
                            />
                        )}

                        {((workspace.campaign.campaign_type === 'commission' && !commissionHasDeliverables) ||
                            (workspace.campaign.app_install?.enabled && !appInstallHasDeliverables)) && (
                            <Box
                                sx={{
                                    p: 2.5,
                                    borderRadius: 2,
                                    bgcolor: 'rgba(255,201,101,0.06)',
                                    border: '1px solid rgba(255,201,101,0.18)',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 1,
                                    mb: 1,
                                }}>
                                <Typography
                                    sx={{ fontFamily: 'inherit', fontWeight: 700, fontSize: 14, color: '#ffc965' }}>
                                    No content submission required
                                </Typography>
                                <Typography sx={{ fontSize: 13, color: 'rgba(255,255,255,0.65)', lineHeight: 1.55 }}>
                                    {workspace.campaign.app_install?.enabled
                                        ? 'This is an app / web install campaign. Your earnings are tracked automatically via your unique referral link — no video or photo upload needed.'
                                        : 'This is a commission campaign. Share your unique promo link to earn. No video or photo upload is needed.'}
                                </Typography>
                            </Box>
                        )}

                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', lg: 'row' },
                                gap: 3,
                                alignItems: 'stretch',
                            }}>
                            {/* Mobile: deliverables first; lg: left column */}
                            {(workspace.campaign.campaign_type !== 'commission' || commissionHasDeliverables) &&
                                (!workspace.campaign.app_install?.enabled || appInstallHasDeliverables) && (
                                    <Box
                                        component="aside"
                                        sx={{
                                            width: { xs: '100%', lg: 400 },
                                            flexShrink: 0,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 3,
                                        }}>
                                        {activeRound ? (
                                            <Box
                                                sx={{
                                                    p: 2,
                                                    borderRadius: 2,
                                                    bgcolor: 'rgba(255,201,101,0.07)',
                                                    border: '1px solid rgba(255,201,101,0.2)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    gap: 1.5,
                                                }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                    <InfoOutlinedIcon
                                                        sx={{ fontSize: 16, color: GOLD, flexShrink: 0 }}
                                                    />
                                                    <Box>
                                                        <Typography
                                                            sx={{
                                                                fontSize: 13,
                                                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                                                color: GOLD,
                                                            }}>
                                                            {activeRound.label ??
                                                                `Delivery ${activeRound.round_number}`}
                                                        </Typography>
                                                        {activeRound.delivery_date ? (
                                                            <Typography
                                                                sx={{
                                                                    fontSize: 11,
                                                                    color: 'rgba(255,255,255,0.55)',
                                                                    mt: 0.25,
                                                                }}>
                                                                Deadline:{' '}
                                                                {(() => {
                                                                    const override = (
                                                                        workspace.participant as any
                                                                    ).round_delivery_overrides?.find(
                                                                        (o: any) =>
                                                                            String(o.round_id) ===
                                                                            String(activeRound._id),
                                                                    );
                                                                    const date =
                                                                        override?.delivery_date ||
                                                                        activeRound.delivery_date;
                                                                    return new Date(date).toLocaleDateString('en-US', {
                                                                        month: 'short',
                                                                        day: 'numeric',
                                                                        year: 'numeric',
                                                                    });
                                                                })()}
                                                            </Typography>
                                                        ) : null}
                                                    </Box>
                                                </Box>
                                                {activeRound.delivery_date && (
                                                    <>
                                                        {(workspace.participant.status === 'in_progress' ||
                                                            workspace.participant.status === 'approved') &&
                                                            !approvedSubmissions.size &&
                                                            !workspace.participant.round_payouts?.some(
                                                                (rp: any) =>
                                                                    String(rp.round_id) === String(activeRound._id) &&
                                                                    rp.initiated_at,
                                                            ) &&
                                                            !(
                                                                workspace.participant as any
                                                            ).delivery_extension_requests?.some(
                                                                (r: any) =>
                                                                    r.status === 'pending' &&
                                                                    String(r.round_id ?? '') ===
                                                                        String(activeRound._id),
                                                            ) && (
                                                                <Button
                                                                    size="small"
                                                                    variant="contained"
                                                                    onClick={() => {
                                                                        setExtensionRoundId(String(activeRound._id));
                                                                        setExtensionDays('');
                                                                        setExtensionReason('');
                                                                        setExtensionDialogOpen(true);
                                                                    }}
                                                                    sx={{
                                                                        flexShrink: 0,
                                                                        fontSize: 10,
                                                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                                                        textTransform: 'none',
                                                                        borderRadius: 999,
                                                                        px: 1.5,
                                                                        py: 0.5,
                                                                        bgcolor: GOLD,
                                                                        color: '#131517',
                                                                        boxShadow: 'none',
                                                                        '&:hover': { bgcolor: GOLD, boxShadow: 'none' },
                                                                    }}>
                                                                    Request extension
                                                                </Button>
                                                            )}
                                                        {(
                                                            workspace.participant as any
                                                        ).delivery_extension_requests?.some(
                                                            (r: any) =>
                                                                r.status === 'pending' &&
                                                                String(r.round_id ?? '') === String(activeRound._id),
                                                        ) && (
                                                            <Typography
                                                                sx={{
                                                                    flexShrink: 0,
                                                                    fontSize: 9,
                                                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                                                    color: MINT,
                                                                }}>
                                                                Extension pending review
                                                            </Typography>
                                                        )}
                                                    </>
                                                )}
                                            </Box>
                                        ) : null}
                                        {(graceRounds.length > 0 ||
                                            pinnedRoundId ||
                                            (!isSubscribed &&
                                                openLiveLinkWindows.some(w =>
                                                    activeRound?._id
                                                        ? String(w.round_id) === String(activeRound._id)
                                                        : !w.round_id,
                                                ))) && (
                                            <Box
                                                sx={{
                                                    p: 2,
                                                    borderRadius: 2,
                                                    bgcolor: 'rgba(255,201,101,0.07)',
                                                    border: '1px solid rgba(255,201,101,0.2)',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: 1,
                                                }}>
                                                <Typography
                                                    sx={{
                                                        fontSize: 13,
                                                        color: 'rgba(255,255,255,0.75)',
                                                        lineHeight: 1.5,
                                                    }}>
                                                    {isSubscribed
                                                        ? 'The brand reopened a past delivery. Upload your content or submit the live link before the window ends.'
                                                        : 'The brand reopened a past delivery. An active subscription is required before you can upload or submit the live link.'}
                                                </Typography>
                                                {graceRounds.map(g => (
                                                    <Button
                                                        key={String(g.round._id)}
                                                        onClick={() => {
                                                            if (!ensureSubscriptionForWindow(g.round._id)) return;
                                                            setPinnedRoundId(String(g.round._id));
                                                            setFiles([]);
                                                            setLocalUploadError(null);
                                                        }}
                                                        sx={{
                                                            alignSelf: 'flex-start',
                                                            textTransform: 'none',
                                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                                            fontSize: 12,
                                                            borderRadius: 999,
                                                            px: 1.75,
                                                            bgcolor: GOLD,
                                                            color: '#131517',
                                                            '&:hover': { bgcolor: GOLD },
                                                        }}>
                                                        {isSubscribed
                                                            ? `Finish Delivery ${
                                                                  g.round.round_number
                                                              } until ${formatLiveLinkWindow(g.expiresAt)}`
                                                            : `Subscribe to finish Delivery ${g.round.round_number}`}
                                                    </Button>
                                                ))}
                                                {!isSubscribed && graceRounds.length === 0 && (
                                                    <Button
                                                        onClick={promptCreatorSubscribe}
                                                        sx={{
                                                            alignSelf: 'flex-start',
                                                            textTransform: 'none',
                                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                                            fontSize: 12,
                                                            borderRadius: 999,
                                                            px: 1.75,
                                                            bgcolor: GOLD,
                                                            color: '#131517',
                                                            '&:hover': { bgcolor: GOLD },
                                                        }}>
                                                        Subscribe
                                                    </Button>
                                                )}
                                                {pinnedRoundId && (
                                                    <Button
                                                        onClick={() => setPinnedRoundId(null)}
                                                        sx={{
                                                            alignSelf: 'flex-start',
                                                            textTransform: 'none',
                                                            fontSize: 12,
                                                            color: GOLD,
                                                        }}>
                                                        Back to the current delivery
                                                    </Button>
                                                )}
                                            </Box>
                                        )}
                                        <DeliverableAndUploadPanel
                                            deliverableType={deliverableType}
                                            onDeliverableType={t => {
                                                setDeliverableType(t);
                                                setSlotIndex(0);
                                                setFiles([]);
                                                setLocalUploadError(null);
                                            }}
                                            deliverableConstraints={(() => {
                                                const active = campaignDeliverables.find(
                                                    d => d.type === deliverableType,
                                                );
                                                return active
                                                    ? {
                                                          durationRange: active.duration_range ?? 'n/a',
                                                          requiresVertical:
                                                              active.type.includes('short') ||
                                                              active.type === 'vertical',
                                                          requiresHorizontal: active.type === 'horizontal',
                                                      }
                                                    : null;
                                            })()}
                                            campaignDeliverables={campaignDeliverables}
                                            submittedTypes={submittedTypes}
                                            revisionTypes={revisionTypes}
                                            totalRequired={totalRequired}
                                            totalSubmitted={totalSubmitted}
                                            requiredCount={requiredCount}
                                            files={files}
                                            onRemoveFile={i => setFiles(prev => prev.filter((_, idx) => idx !== i))}
                                            fileInputRef={fileInputRef}
                                            onFilesPicked={onFilesPicked}
                                            onBrowse={() => fileInputRef.current?.click()}
                                            onSubmit={handleSubmitForApproval}
                                            submitting={submitting}
                                            uploadProgress={uploadProgress}
                                            errorMessage={panelErr}
                                            fullSubmitPath={submitPath}
                                            onNavigateFullSubmit={() => submitPath && navigate(submitPath)}
                                            participationBlocked={deliveryActionBlocked}
                                            participationLockMessage={
                                                deliveryActionBlocked ? participationLockMessage : ''
                                            }
                                            campaignPlatforms={(() => {
                                                const fromDeliverables = (workspace?.campaign.deliverables ?? [])
                                                    .map((d: any) => d.platform)
                                                    .filter(Boolean) as string[];
                                                const fromCampaign = (workspace?.campaign.platforms ?? []) as string[];
                                                return [...new Set([...fromCampaign, ...fromDeliverables])];
                                            })()}
                                            multiUpload={activeDeliverableMultiUpload}
                                            slotIndex={slotIndex}
                                            submittedSlots={submittedSlotsByType.get(deliverableType)}
                                            onSlotChange={i => {
                                                setSlotIndex(i);
                                                setFiles([]);
                                                setLocalUploadError(null);
                                            }}
                                        />
                                        {selectedGrace && graceApproved.size > 0 && (
                                            <GoLivePanel
                                                approvedSubmissions={graceApproved}
                                                activeType={graceLiveType}
                                                onTypeChange={t => {
                                                    setGraceLiveType(t);
                                                    setGraceLiveInputs([]);
                                                }}
                                                liveUrlInputs={graceLiveInputs}
                                                onLiveUrlChange={(idx, val) =>
                                                    setGraceLiveInputs(prev => {
                                                        const next = [...prev];
                                                        next[idx] = val;
                                                        return next;
                                                    })
                                                }
                                                onSubmit={handleSubmitGraceLiveUrl}
                                                submitting={submittingLiveUrl}
                                                participationBlocked={deliveryActionBlocked}
                                                participationLockMessage={
                                                    deliveryActionBlocked ? liveUrlLockMessage : ''
                                                }
                                                windowNote={`Delivery ${
                                                    selectedGrace.round.round_number
                                                } is open until ${formatLiveLinkWindow(
                                                    selectedGrace.expiresAt,
                                                )}. Submit the live link here.`}
                                            />
                                        )}
                                        {approvedSubmissions.size > 0 && (
                                            <GoLivePanel
                                                approvedSubmissions={approvedSubmissions}
                                                windowNote={activeWindowNote}
                                                activeType={activeLiveUrlType}
                                                onTypeChange={t => {
                                                    setActiveLiveUrlType(t);
                                                    setLiveUrlInputs([]);
                                                }}
                                                liveUrlInputs={liveUrlInputs}
                                                onLiveUrlChange={(idx, val) =>
                                                    setLiveUrlInputs(prev => {
                                                        const next = [...prev];
                                                        next[idx] = val;
                                                        return next;
                                                    })
                                                }
                                                onSubmit={handleSubmitLiveUrl}
                                                submitting={submittingLiveUrl}
                                                isDirectHire={
                                                    workspace.campaign.campaign_type === 'direct_hire' ||
                                                    workspace.campaign.campaign_type === 'commission'
                                                }
                                                syncPublishDate={
                                                    workspace.campaign.campaign_type === 'sync_upload'
                                                        ? workspace.campaign.sync_publish_date
                                                        : undefined
                                                }
                                                participationBlocked={deliveryActionBlocked}
                                                participationLockMessage={
                                                    deliveryActionBlocked ? liveUrlLockMessage : ''
                                                }
                                                onPublishSuccess={platformPostUrl => {
                                                    if (
                                                        deliveryActionBlocked ||
                                                        !ensureSubscriptionForWindow(activeRound?._id || null) ||
                                                        !pid ||
                                                        !approvedSubmissions.get(activeLiveUrlType)?._id
                                                    )
                                                        return;
                                                    dispatch(
                                                        submitLiveUrl({
                                                            participantId: pid,
                                                            submissionId:
                                                                approvedSubmissions.get(activeLiveUrlType)!._id!,
                                                            liveUrls: [platformPostUrl],
                                                        }),
                                                    );
                                                }}
                                            />
                                        )}
                                    </Box>
                                )}

                            {/* lg: inline thread; xs: fixed bottom sheet (see below) */}
                            <Box
                                sx={{
                                    display: { xs: 'none', lg: 'flex' },
                                    flex: '1 1 0%',
                                    minWidth: 0,
                                    width: 'auto',
                                    flexDirection: 'column',
                                    height: 650,
                                    minHeight: 400,
                                    bgcolor: SURFACE_LOW,
                                    borderRadius: 2,
                                    overflow: 'hidden',
                                    border: '1px solid rgba(255,255,255,0.05)',
                                }}>
                                <ChatChrome workspace={workspace} />
                                <ParticipantWorkspaceDiscussion
                                    participantId={pid}
                                    campaignId={workspace.campaign._id}
                                    messageType="all_participants"
                                    messages={workspace.messages ?? []}
                                    creatorDisplayName={creatorName}
                                    counterpartDisplayName={brandCounterpartName}
                                    counterpartUserIds={brandCounterpartIds}
                                    currentUserId={currentUserId}
                                    hasMoreOlder={Boolean(workspace.messages_pagination?.has_more_older)}
                                    loadingOlder={loadingOlder}
                                    onLoadOlder={loadOlder}
                                    variant="creator"
                                    contentSubmissions={workspace.participant.content_submissions}
                                    participationBlocked={participationBlocked}
                                />
                            </Box>
                        </Box>

                        <Box sx={{ display: { xs: 'block', lg: 'none' } }}>
                            {mobileChatExpanded && (
                                <Box
                                    aria-hidden
                                    onClick={() => setMobileChatExpanded(false)}
                                    sx={{
                                        position: 'fixed',
                                        inset: 0,
                                        zIndex: 1380,
                                        bgcolor: 'rgba(0,0,0,0.45)',
                                    }}
                                />
                            )}
                            <Box
                                sx={{
                                    position: 'fixed',
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    zIndex: 1390,
                                    pb: 'env(safe-area-inset-bottom, 0px)',
                                }}>
                                <Box
                                    sx={{
                                        borderTopLeftRadius: 16,
                                        borderTopRightRadius: 16,
                                        bgcolor: SURFACE_LOW,
                                        border: '1px solid rgba(255,255,255,0.05)',
                                        boxShadow: '0 -12px 40px rgba(0,0,0,0.55)',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        maxHeight: mobileChatExpanded ? 'min(88dvh, 860px)' : 88,
                                        transition: 'max-height 0.28s ease',
                                        overflow: 'hidden',
                                        mx: { xs: 0, sm: 1 },
                                    }}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'stretch',
                                            flexShrink: 0,
                                            minHeight: 88,
                                        }}>
                                        <Box
                                            role="button"
                                            tabIndex={0}
                                            onClick={() => {
                                                if (!mobileChatExpanded) setMobileChatExpanded(true);
                                            }}
                                            onKeyDown={e => {
                                                if (!mobileChatExpanded && (e.key === 'Enter' || e.key === ' ')) {
                                                    e.preventDefault();
                                                    setMobileChatExpanded(true);
                                                }
                                            }}
                                            sx={{
                                                flex: 1,
                                                minWidth: 0,
                                                cursor: mobileChatExpanded ? 'default' : 'pointer',
                                            }}>
                                            <ChatChrome workspace={workspace} />
                                        </Box>
                                        {mobileChatExpanded && (
                                            <IconButton
                                                aria-label="Close messages"
                                                onClick={e => {
                                                    e.stopPropagation();
                                                    setMobileChatExpanded(false);
                                                }}
                                                sx={{
                                                    flexShrink: 0,
                                                    alignSelf: 'flex-start',
                                                    mt: 1,
                                                    mr: 0.5,
                                                    color: ON_VARIANT,
                                                }}>
                                                <CloseIcon />
                                            </IconButton>
                                        )}
                                    </Box>
                                    {mobileChatExpanded && (
                                        <Box
                                            sx={{
                                                flex: 1,
                                                minHeight: 200,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                overflow: 'hidden',
                                                borderTop: '1px solid rgba(255,255,255,0.05)',
                                            }}>
                                            <ParticipantWorkspaceDiscussion
                                                participantId={pid}
                                                campaignId={workspace.campaign._id}
                                                messageType="all_participants"
                                                messages={workspace.messages ?? []}
                                                creatorDisplayName={creatorName}
                                                counterpartDisplayName={brandCounterpartName}
                                                counterpartUserIds={brandCounterpartIds}
                                                currentUserId={currentUserId}
                                                hasMoreOlder={Boolean(workspace.messages_pagination?.has_more_older)}
                                                loadingOlder={loadingOlder}
                                                onLoadOlder={loadOlder}
                                                variant="creator"
                                                contentSubmissions={workspace.participant.content_submissions}
                                                participationBlocked={participationBlocked}
                                            />
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        </Box>
                    </>
                ) : null}
            </Box>

            <Dialog
                open={detailsOpen}
                onClose={() => setDetailsOpen(false)}
                fullWidth
                maxWidth="sm"
                PaperProps={{
                    sx: {
                        bgcolor: SURFACE,
                        borderRadius: 3,
                        maxHeight: '90vh',
                        backgroundImage: 'none',
                        boxShadow: '0 20px 40px rgba(0,0,0,0.6)',
                    },
                }}
                slotProps={{ backdrop: { sx: { bgcolor: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(4px)' } } }}>
                <Box sx={{ position: 'relative' }}>
                    <IconButton
                        onClick={() => setDetailsOpen(false)}
                        size="small"
                        sx={{ position: 'absolute', top: 12, right: 12, color: ON_VARIANT, zIndex: 1 }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>

                    {/* Header */}
                    <Box
                        sx={{
                            px: 3,
                            pt: 3,
                            pb: 2,
                            borderBottom: '1px solid rgba(255,255,255,0.06)',
                        }}>
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 11,
                                letterSpacing: '0.15em',
                                textTransform: 'uppercase',
                                color: ON_VARIANT,
                                mb: 0.5,
                            }}>
                            Campaign details
                        </Typography>
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontWeight: 800,
                                fontSize: 20,
                                color: GOLD,
                                lineHeight: 1.2,
                                pr: 4,
                            }}>
                            {workspace?.campaign.title ?? '—'}
                        </Typography>
                    </Box>

                    {/* Body */}
                    <DialogContent sx={{ px: 3, py: 2.5 }}>
                        {workspace ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                                <Box>
                                    <Typography
                                        sx={{
                                            fontSize: 10,
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            letterSpacing: '0.1em',
                                            textTransform: 'uppercase',
                                            color: ON_VARIANT,
                                            mb: 0.75,
                                        }}>
                                        Description
                                    </Typography>
                                    <Box
                                        sx={{
                                            '& .rich-text-wrapper .ql-editor': {
                                                fontSize: 14,
                                                lineHeight: 1.7,
                                                color: 'rgba(255,255,255,0.8) !important',
                                            },
                                        }}>
                                        {workspace.campaign.description ? (
                                            <RichTextViewer content={workspace.campaign.description} />
                                        ) : (
                                            <Typography sx={{ fontSize: 14, color: 'rgba(255,255,255,0.8)' }}>
                                                —
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>

                                {workspace.campaign.requirements && workspace.campaign.requirements.length > 0 && (
                                    <Box>
                                        <Typography
                                            sx={{
                                                fontSize: 10,
                                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                                letterSpacing: '0.1em',
                                                textTransform: 'uppercase',
                                                color: ON_VARIANT,
                                                mb: 0.75,
                                            }}>
                                            Requirements
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                            {workspace.campaign.requirements.map((req, i) => (
                                                <Box
                                                    key={i}
                                                    sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                                    <CheckCircleIcon
                                                        sx={{
                                                            fontSize: 16,
                                                            color: '#22c55e',
                                                            flexShrink: 0,
                                                            mt: '2px',
                                                        }}
                                                    />
                                                    <Typography
                                                        sx={{
                                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                            fontSize: 14,
                                                            color: 'rgba(255,255,255,0.8)',
                                                            lineHeight: 1.6,
                                                        }}>
                                                        {req}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        ) : null}
                    </DialogContent>
                </Box>
            </Dialog>

            {workspace?.brand?._id ? (
                <BrandReviewModal
                    open={brandReviewOpen}
                    onClose={() => setBrandReviewOpen(false)}
                    brandId={workspace.brand._id}
                    brandName={workspace.brand.name}
                    brandProfilePicture={
                        typeof workspace.brand.profile_picture === 'string'
                            ? workspace.brand.profile_picture
                            : undefined
                    }
                    mode={workspace.brand_review?.can_update ? 'edit' : 'create'}
                    initialRating={workspace.brand_review?.review?.rating ?? 0}
                    initialReviewText={workspace.brand_review?.review?.review_text ?? ''}
                    reviewId={workspace.brand_review?.review?._id}
                    suggestedCampaignParticipant={workspace.brand_review?.suggested_campaign_participant ?? null}
                    participantId={pid}
                />
            ) : null}

            {/* Upload progress modal — blocks re-submission while S3 upload is in flight */}
            <Dialog
                open={(uploadProgress > 0 && uploadProgress < 100) || uploadFailed}
                disableEscapeKeyDown={!uploadFailed}
                onClose={uploadFailed ? () => setUploadFailed(false) : undefined}
                PaperProps={{
                    sx: {
                        bgcolor: '#161616',
                        borderRadius: 3,
                        p: 4,
                        maxWidth: 400,
                        width: '100%',
                        textAlign: 'center',
                        border: `1px solid ${uploadFailed ? 'rgba(255,107,107,0.2)' : 'rgba(255,255,255,0.06)'}`,
                    },
                }}>
                {uploadFailed ? (
                    <>
                        <Box sx={{ mb: 3 }}>
                            <Typography sx={{ fontSize: 40, mb: 1 }}>⚠️</Typography>
                            <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 18, color: '#ff6b6b', mb: 1 }}>
                                Upload failed
                            </Typography>
                            <Typography sx={{ fontSize: 13, color: ON_VARIANT, lineHeight: 1.6 }}>
                                Your submission was recorded but the video failed to upload. Please check your
                                connection and try submitting again. If the problem persists, contact support.
                            </Typography>
                        </Box>
                        <Button
                            fullWidth
                            onClick={() => setUploadFailed(false)}
                            sx={{
                                py: 1.5,
                                borderRadius: 2,
                                textTransform: 'none',
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 13,
                                bgcolor: 'rgba(255,255,255,0.08)',
                                color: '#fff',
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.12)' },
                            }}>
                            Dismiss
                        </Button>
                    </>
                ) : (
                    <>
                        <Box sx={{ mb: 3 }}>
                            <CloudUploadOutlinedIcon sx={{ fontSize: 48, color: GOLD, mb: 1.5 }} />
                            <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 18, color: '#fff', mb: 1 }}>
                                Uploading your content
                            </Typography>
                            <Typography sx={{ fontSize: 13, color: ON_VARIANT, lineHeight: 1.6 }}>
                                Please wait while your video is being uploaded. Do not close this page or submit again —
                                this may take a moment depending on your file size and connection speed.
                            </Typography>
                        </Box>
                        <Box sx={{ mb: 1.5 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography sx={{ fontSize: 12, color: ON_VARIANT }}>Upload progress</Typography>
                                <Typography sx={{ fontSize: 12, fontFamily: FONT_ENUM.MAIN_BOLD, color: GOLD }}>
                                    {uploadProgress}%
                                </Typography>
                            </Box>
                            <LinearProgress
                                variant="determinate"
                                value={uploadProgress}
                                sx={{
                                    height: 6,
                                    borderRadius: 4,
                                    bgcolor: 'rgba(255,255,255,0.08)',
                                    '& .MuiLinearProgress-bar': { bgcolor: GOLD, borderRadius: 4 },
                                }}
                            />
                        </Box>
                        <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>
                            Your submission has been recorded — this window will close once the upload is complete.
                        </Typography>
                        <Button
                            fullWidth
                            onClick={handleCancelUpload}
                            sx={{
                                mt: 2,
                                py: 1.5,
                                borderRadius: 2,
                                textTransform: 'none',
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 13,
                                bgcolor: 'rgba(255,255,255,0.06)',
                                color: '#9e9e9e',
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.1)', color: '#fff' },
                            }}>
                            Cancel upload
                        </Button>
                    </>
                )}
            </Dialog>

            {/* Opt-out confirmation dialog */}
            <Dialog
                open={optOutDialogOpen}
                onClose={() => !withdrawing && setOptOutDialogOpen(false)}
                PaperProps={{
                    sx: {
                        bgcolor: SURFACE_LOW,
                        border: '1px solid rgba(198,40,40,0.3)',
                        borderRadius: 3,
                        px: 3,
                        py: 3,
                        maxWidth: 420,
                        width: '100%',
                    },
                }}>
                <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 18, color: '#fff', mb: 1 }}>
                    Opt out of this campaign?
                </Typography>
                <Typography sx={{ fontSize: 13, color: 'rgba(255,255,255,0.65)', lineHeight: 1.65, mb: 3 }}>
                    You are about to withdraw from this campaign. This action{' '}
                    <Typography
                        component="span"
                        sx={{ color: '#ef5350', fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 13 }}>
                        cannot be reversed
                    </Typography>
                    . The brand will be notified and your participation will be removed.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
                    <Button
                        variant="outlined"
                        disabled={withdrawing}
                        onClick={() => setOptOutDialogOpen(false)}
                        sx={{
                            borderColor: 'rgba(255,255,255,0.15)',
                            color: 'rgba(255,255,255,0.7)',
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 13,
                            textTransform: 'none',
                            '&:hover': { borderColor: 'rgba(255,255,255,0.3)', bgcolor: 'rgba(255,255,255,0.04)' },
                        }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        disabled={withdrawing}
                        onClick={() => {
                            dispatch(withdrawFromCampaign({ participantId: pid }));
                        }}
                        sx={{
                            bgcolor: '#c62828',
                            color: '#fff',
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 13,
                            textTransform: 'none',
                            '&:hover': { bgcolor: '#b71c1c' },
                            '&:disabled': { bgcolor: 'rgba(198,40,40,0.4)', color: 'rgba(255,255,255,0.5)' },
                        }}>
                        {withdrawing ? <CircularProgress size={14} sx={{ color: '#fff', mr: 0.75 }} /> : null}
                        Yes, opt out
                    </Button>
                </Box>
            </Dialog>

            {/* Delivery date extension request dialog */}
            <Dialog
                open={extensionDialogOpen}
                onClose={() => !requestingExtension && setExtensionDialogOpen(false)}
                PaperProps={{
                    sx: {
                        bgcolor: SURFACE_LOW,
                        border: '1px solid rgba(255,255,255,0.08)',
                        borderRadius: 2,
                        p: 3,
                        maxWidth: 400,
                        width: '100%',
                    },
                }}>
                <Typography sx={{ fontSize: 16, fontFamily: FONT_ENUM.MAIN_BOLD, color: '#fff', mb: 1 }}>
                    Request Delivery Extension
                </Typography>
                <Typography sx={{ fontSize: 13, color: 'rgba(255,255,255,0.6)', mb: 2 }}>
                    Request more time to deliver your content. The brand will review and approve or decline your
                    request.
                </Typography>
                <Typography sx={{ fontSize: 12, color: ON_VARIANT, mb: 0.5 }}>Additional days needed</Typography>
                <Select
                    value={extensionDays}
                    onChange={e => {
                        const val = e.target.value as string;
                        if (!isSubscribed && Number(val) > 2) {
                            dispatch(toggleSubscriptionModal(true));
                            return;
                        }
                        setExtensionDays(val);
                    }}
                    displayEmpty
                    size="small"
                    fullWidth
                    disabled={requestingExtension}
                    sx={{
                        mb: 1.5,
                        bgcolor: SURFACE_HI,
                        color: extensionDays ? '#fff' : 'rgba(255,255,255,0.35)',
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.15)' },
                        '& .MuiSvgIcon-root': { color: 'rgba(255,255,255,0.5)' },
                        '& .MuiSelect-select': { color: extensionDays ? '#fff' : 'rgba(255,255,255,0.35)' },
                    }}>
                    <MenuItem value="" disabled>
                        Select days
                    </MenuItem>
                    {[1, 2, 3, 4, 5, 6, 7].map(d => (
                        <MenuItem key={d} value={String(d)}>
                            {d} {d === 1 ? 'day' : 'days'}
                            {!isSubscribed && d > 2 ? ' — Pro' : ''}
                        </MenuItem>
                    ))}
                </Select>
                {!isSubscribed ? (
                    <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', mb: 2, lineHeight: 1.55 }}>
                        Free accounts can request up to 2 extra days.{' '}
                        <Box
                            component="span"
                            onClick={() => dispatch(toggleSubscriptionModal(true))}
                            sx={{ color: GOLD, cursor: 'pointer', fontFamily: FONT_ENUM.MAIN_BOLD }}>
                            Upgrade to Pro
                        </Box>{' '}
                        to request up to 7 days.
                    </Typography>
                ) : (
                    <Box sx={{ mb: 2 }} />
                )}
                <Typography sx={{ fontSize: 12, color: ON_VARIANT, mb: 0.5 }}>Reason (optional)</Typography>
                <TextField
                    value={extensionReason}
                    onChange={e => setExtensionReason(e.target.value)}
                    multiline
                    minRows={2}
                    fullWidth
                    disabled={requestingExtension}
                    placeholder="Briefly explain why you need more time…"
                    sx={{
                        mb: 2.5,
                        '& .MuiOutlinedInput-root': {
                            bgcolor: SURFACE_HI,
                            color: '#fff',
                            '& fieldset': { borderColor: 'rgba(255,255,255,0.15)' },
                        },
                        '& textarea': { color: '#fff' },
                        '& ::placeholder': { color: 'rgba(255,255,255,0.3)' },
                    }}
                />
                <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
                    <Button
                        variant="outlined"
                        disabled={requestingExtension}
                        onClick={() => setExtensionDialogOpen(false)}
                        sx={{
                            borderColor: 'rgba(255,255,255,0.15)',
                            color: 'rgba(255,255,255,0.7)',
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 13,
                            textTransform: 'none',
                            '&:hover': { borderColor: 'rgba(255,255,255,0.3)', bgcolor: 'rgba(255,255,255,0.04)' },
                        }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        disabled={requestingExtension || !extensionDays}
                        onClick={() => {
                            dispatch(
                                requestDeliveryExtension({
                                    participantId: pid,
                                    requestedDays: Number(extensionDays),
                                    reason: extensionReason.trim() || undefined,
                                    round_id: extensionRoundId,
                                }),
                            );
                            setExtensionDialogOpen(false);
                        }}
                        sx={{
                            bgcolor: GOLD,
                            color: '#000',
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 13,
                            textTransform: 'none',
                            '&:hover': { bgcolor: '#e6b400' },
                            '&:disabled': {
                                bgcolor: 'rgba(255,201,101,0.3)',
                                color: 'rgba(0,0,0,0.4)',
                            },
                        }}>
                        {requestingExtension ? <CircularProgress size={14} sx={{ color: '#000', mr: 0.75 }} /> : null}
                        Submit request
                    </Button>
                </Box>
            </Dialog>
        </Box>
    );
}

function brandReviewReasonCopy(reason: ICreatorBrandReviewContext['reason']): string | null {
    switch (reason) {
        case 'no_completed_campaign':
            return 'Complete a campaign with this brand before you can leave a review.';
        case 'not_influencer':
            return 'Only influencer accounts can rate brands.';
        case 'already_reviewed':
            return null;
        default:
            return null;
    }
}

function BrandReviewPrompt({
    workspace,
    onOpenReview,
}: {
    workspace: ICampaignWorkspaceData;
    onOpenReview: () => void;
}) {
    const ctx = workspace.brand_review;
    if (!ctx) return null;

    // Eligibility is per brand (any completed participation), not this row's status.
    const showCta = ctx.eligible || ctx.can_update;
    const reasonMsg = !showCta ? brandReviewReasonCopy(ctx.reason) : null;
    const existingRating = ctx.review?.rating;
    const avg = ctx.brand_rating?.average_rating;
    const count = ctx.brand_rating?.review_count;

    if (!showCta && !reasonMsg) return null;

    return (
        <Box
            sx={{
                mb: 3,
                p: 2.5,
                borderRadius: 2,
                bgcolor: SURFACE_LOW,
                border: '1px solid rgba(255,201,101,0.12)',
                boxShadow: 'inset 0 0 12px rgba(255, 193, 7, 0.04)',
            }}>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { sm: 'center' },
                    gap: 2,
                }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        sx={{
                            fontSize: 10,
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            letterSpacing: '0.18em',
                            textTransform: 'uppercase',
                            color: ON_VARIANT,
                            mb: 0.5,
                        }}>
                        Brand review
                    </Typography>
                    {showCta ? (
                        <>
                            <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 16, color: GOLD }}>
                                {ctx.can_update ? 'You reviewed this brand' : 'How was your collaboration?'}
                            </Typography>
                            <Typography
                                sx={{ fontSize: 13, color: 'rgba(255,255,255,0.65)', mt: 0.5, lineHeight: 1.5 }}>
                                {ctx.can_update
                                    ? 'Update your star rating or written feedback anytime.'
                                    : 'Rate the brand based on your completed campaign experience.'}
                            </Typography>
                            {ctx.can_update && existingRating ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                                    {Array.from({ length: 5 }).map((_, i) => (
                                        <StarIcon
                                            key={i}
                                            sx={{
                                                fontSize: 18,
                                                color: i < Math.round(existingRating) ? GOLD : 'rgba(255,255,255,0.2)',
                                            }}
                                        />
                                    ))}
                                    <Typography sx={{ ml: 1, fontSize: 13, color: ON_VARIANT }}>
                                        Your rating: {existingRating}/5
                                    </Typography>
                                </Box>
                            ) : null}
                            {typeof avg === 'number' && count ? (
                                <Typography sx={{ fontSize: 12, color: ON_VARIANT, mt: 0.75 }}>
                                    Brand average: {avg.toFixed(1)} ({count} review{count === 1 ? '' : 's'})
                                </Typography>
                            ) : null}
                        </>
                    ) : (
                        <Typography sx={{ fontSize: 14, color: 'rgba(255,255,255,0.7)', lineHeight: 1.5 }}>
                            {reasonMsg}
                        </Typography>
                    )}
                </Box>
                {showCta ? (
                    <Button
                        variant="contained"
                        startIcon={<RateReviewOutlinedIcon />}
                        onClick={onOpenReview}
                        sx={{
                            flexShrink: 0,
                            textTransform: 'none',
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            borderRadius: 999,
                            px: 2.5,
                            bgcolor: GOLD,
                            color: '#131517',
                            boxShadow: 'none',
                            '&:hover': { bgcolor: GOLD, boxShadow: 'none' },
                        }}>
                        {ctx.can_update ? 'Edit review' : 'Rate brand'}
                    </Button>
                ) : null}
            </Box>
        </Box>
    );
}

function ClippingSourceBanner({ url }: { url: string }) {
    return (
        <Box
            sx={{
                mb: 3,
                p: { xs: 2, sm: 2.5 },
                bgcolor: SURFACE_LOW,
                borderRadius: 2,
                border: `1px solid rgba(255,201,101,0.22)`,
                boxShadow: 'inset 0 0 20px rgba(255, 193, 7, 0.04)',
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'stretch', sm: 'center' },
                justifyContent: 'space-between',
                gap: 2,
            }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, minWidth: 0 }}>
                <Box
                    sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        bgcolor: 'rgba(255,201,101,0.12)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                    }}>
                    <ContentCutOutlinedIcon sx={{ color: GOLD, fontSize: 22 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 15,
                            color: GOLD,
                            mb: 0.5,
                        }}>
                        Clipping source video
                    </Typography>
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            fontSize: 13,
                            color: ON_VARIANT,
                            lineHeight: 1.5,
                        }}>
                        Download the brand&apos;s raw footage, then edit it into your deliverables for this campaign.
                    </Typography>
                </Box>
            </Box>
            <Button
                component="a"
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<DownloadOutlinedIcon />}
                sx={{
                    flexShrink: 0,
                    alignSelf: { xs: 'stretch', sm: 'center' },
                    textTransform: 'none',
                    fontFamily: FONT_ENUM.MAIN_BOLD,
                    fontSize: 13,
                    borderRadius: 999,
                    px: 2.5,
                    py: 1.1,
                    bgcolor: GOLD,
                    color: '#131517',
                    boxShadow: 'none',
                    '&:hover': { bgcolor: GOLD, boxShadow: 'none' },
                }}>
                Download source
            </Button>
        </Box>
    );
}

function TopJobBanner({
    workspace,
    activeRound,
    onOpenDetails,
    onRequestExtension,
}: {
    workspace: ICampaignWorkspaceData;
    activeRound: IDeliverableRound | null;
    onOpenDetails: () => void;
    onRequestExtension?: () => void;
}) {
    const st = statusUi(workspace.participant.status);
    const campaign = workspace.campaign as any;
    const isRecurrent = Boolean(campaign.recurrent_deliverables);
    const allRounds: IDeliverableRound[] = isRecurrent ? campaign.deliverable_rounds ?? [] : [];
    const totalRounds = allRounds.length;
    const activeRoundIndex = activeRound
        ? allRounds.findIndex(
              r => String(r._id) === String(activeRound._id) || r.round_number === activeRound.round_number,
          )
        : -1;
    const currentRoundNumber = activeRoundIndex >= 0 ? activeRoundIndex + 1 : activeRound?.round_number ?? 1;

    const fmt = (n: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
    const totalReward = (() => {
        const pe = workspace.possible_earning;
        if (typeof pe === 'number' && !Number.isNaN(pe)) return pe;
        return null;
    })();
    const reward = totalReward != null ? fmt(totalReward) : rewardFromCampaign(workspace.campaign);

    const perDeliveryReward = (() => {
        if (!isRecurrent || totalRounds < 2) return null;
        // Use base_payment_per_creator_cents if available (app_install / commission type)
        const base = campaign.base_payment_per_creator_cents;
        if (typeof base === 'number' && base > 0) return fmt(base / 100);
        // Otherwise divide total by rounds
        if (totalReward != null && totalRounds > 0) return fmt(totalReward / totalRounds);
        return null;
    })();
    const dl = deadlineBlock(workspace);
    const [copiedLink, setCopiedLink] = useState(false);
    const [activityOpen, setActivityOpen] = useState(false);
    const isCommission = workspace.campaign.campaign_type === 'commission';
    const isCircuul = Boolean(workspace.circuul?.code || workspace.circuul_stats);
    const commissionShareUrl = useMemo(() => {
        if (!isCommission) return null;
        const code = workspace.commission?.code || '';
        if (!code) return null;
        const contestSlug = workspace.commission?.contest_slug || workspace.campaign.commission_contest?.slug || null;
        return (
            localizeCommissionShareUrl(workspace.commission?.share_url) ||
            buildCommissionShareUrl({
                code,
                kind: workspace.commission?.target_type || 'contest',
                contestSlug,
                shareUrlFromApi: workspace.commission?.share_url,
            }) ||
            (typeof window !== 'undefined'
                ? `${window.location.origin}/r/${encodeURIComponent(code.toUpperCase())}`
                : null)
        );
    }, [workspace, isCommission]);

    const circuulShareUrl = useMemo(() => {
        if (!isCircuul) return null;
        const code = workspace.circuul?.code || workspace.circuul_stats?.code || '';
        if (!code) return null;
        return (
            normalizeApiCircuulShareUrl(workspace.circuul?.share_url) ||
            buildFrontendReferralUrl(code) ||
            buildCommissionShareUrl({
                code,
                kind: workspace.circuul?.kind || workspace.circuul_stats?.kind || 'app_install',
                shareUrlFromApi: workspace.circuul?.share_url,
            })
        );
    }, [workspace, isCircuul]);

    const shareUrl = circuulShareUrl || commissionShareUrl;
    const showActivity = isCommission || isCircuul;

    const handleCopyPromoLink = async () => {
        if (!shareUrl) return;
        try {
            await navigator.clipboard.writeText(shareUrl);
            setCopiedLink(true);
            setTimeout(() => setCopiedLink(false), 2000);
        } catch {
            showToastMessage('Could not copy link', 'error');
        }
    };

    return (
        <>
            <Box
                sx={{
                    bgcolor: SURFACE_LOW,
                    border: '1px solid rgba(255,255,255,0.05)',
                    borderRadius: 2,
                    p: 3,
                    mb: 3,
                    boxShadow: 'inset 0 0 15px rgba(255, 193, 7, 0.05)',
                }}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', lg: 'row' },
                        alignItems: { lg: 'center' },
                        justifyContent: 'space-between',
                        gap: 3,
                    }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box>
                            <Typography
                                sx={{
                                    fontSize: 10,
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    letterSpacing: '0.2em',
                                    textTransform: 'uppercase',
                                    color: ON_VARIANT,
                                }}>
                                Active Project
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                                <Typography
                                    component="a"
                                    href={`/campaigns/${workspace.campaign._id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontWeight: 800,
                                        fontSize: { xs: 22, sm: 24 },
                                        color: GOLD,
                                        lineHeight: 1.2,
                                        textDecoration: 'none',
                                        '&:hover': { textDecoration: 'underline' },
                                    }}>
                                    {workspace.campaign.title ?? 'Campaign'}
                                </Typography>
                                <Tooltip title="View details" placement="top">
                                    <IconButton
                                        onClick={onOpenDetails}
                                        size="small"
                                        sx={{
                                            color: ON_VARIANT,
                                            '&:hover': { color: GOLD, bgcolor: 'rgba(255,201,101,0.06)' },
                                        }}>
                                        <InfoOutlinedIcon sx={{ fontSize: 20 }} />
                                    </IconButton>
                                </Tooltip>
                                {shareUrl ? (
                                    <Button
                                        size="small"
                                        onClick={() => void handleCopyPromoLink()}
                                        startIcon={
                                            copiedLink ? (
                                                <CheckIcon sx={{ fontSize: 16 }} />
                                            ) : (
                                                <ContentCopyIcon sx={{ fontSize: 16 }} />
                                            )
                                        }
                                        sx={{
                                            ml: { xs: 0, sm: 0.5 },
                                            color: ON_VARIANT,
                                            textTransform: 'none',
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            fontSize: 12,
                                            border: '1px solid rgba(255,255,255,0.1)',
                                            borderRadius: 999,
                                            px: 1.5,
                                            py: 0.5,
                                            '&:hover': {
                                                color: GOLD,
                                                borderColor: 'rgba(255,201,101,0.35)',
                                                bgcolor: 'rgba(255,201,101,0.06)',
                                            },
                                        }}>
                                        {copiedLink ? 'Copied' : isCircuul ? 'Copy referral link' : 'Copy link'}
                                    </Button>
                                ) : null}
                                {showActivity ? (
                                    <Button
                                        size="small"
                                        onClick={() => setActivityOpen(true)}
                                        startIcon={<HistoryOutlinedIcon sx={{ fontSize: 16 }} />}
                                        sx={{
                                            color: ON_VARIANT,
                                            textTransform: 'none',
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            fontSize: 12,
                                            border: '1px solid rgba(255,255,255,0.1)',
                                            borderRadius: 999,
                                            px: 1.5,
                                            py: 0.5,
                                            '&:hover': {
                                                color: GOLD,
                                                borderColor: 'rgba(255,201,101,0.35)',
                                                bgcolor: 'rgba(255,201,101,0.06)',
                                            },
                                        }}>
                                        View activity
                                    </Button>
                                ) : null}
                            </Box>
                            {workspace.commission?.code ? (
                                <Typography
                                    sx={{
                                        mt: 0.75,
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontSize: 12,
                                        color: ON_VARIANT,
                                        letterSpacing: '0.04em',
                                    }}>
                                    Promo code{' '}
                                    <Box component="span" sx={{ color: GOLD }}>
                                        {workspace.commission.code}
                                    </Box>
                                    {workspace.commission.rate != null ? ` · ${workspace.commission.rate}%` : ''}
                                </Typography>
                            ) : null}
                            {workspace.circuul?.code ? (
                                <Typography
                                    sx={{
                                        mt: 0.75,
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontSize: 12,
                                        color: ON_VARIANT,
                                        letterSpacing: '0.04em',
                                    }}>
                                    Referral code{' '}
                                    <Box component="span" sx={{ color: GOLD }}>
                                        {workspace.circuul.code}
                                    </Box>
                                </Typography>
                            ) : null}
                        </Box>
                    </Box>

                    {isCircuul || isCommission ? (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', sm: 'row' },
                                alignItems: { sm: 'flex-start' },
                                gap: { xs: 2, sm: 3 },
                                flexShrink: 0,
                            }}>
                            <Box>
                                <Typography
                                    sx={{ fontSize: 10, color: ON_VARIANT, textTransform: 'uppercase', mb: 0.5 }}>
                                    Status
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: 14,
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        color: st.dot,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                    }}>
                                    <Box
                                        sx={{
                                            width: 8,
                                            height: 8,
                                            borderRadius: '50%',
                                            bgcolor: st.dot,
                                            boxShadow: st.dot === MINT ? '0 0 10px #00fdc1' : undefined,
                                        }}
                                    />
                                    {st.label}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Box>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'flex-end',
                                            mb: 0.5,
                                        }}>
                                        <Typography
                                            sx={{ fontSize: 10, color: ON_VARIANT, textTransform: 'uppercase' }}>
                                            Deadline
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontSize: 10,
                                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                                color: GOLD,
                                                ml: 2,
                                            }}>
                                            {dl.dateLine}
                                        </Typography>
                                    </Box>
                                    <Box
                                        sx={{
                                            width: 128,
                                            height: 6,
                                            bgcolor: SURFACE_HI,
                                            borderRadius: 999,
                                            overflow: 'hidden',
                                        }}>
                                        <Box
                                            sx={{
                                                height: '100%',
                                                width: `${dl.pct}%`,
                                                bgcolor: MINT,
                                                borderRadius: 999,
                                                boxShadow: '0 0 8px #00fdc1',
                                            }}
                                        />
                                    </Box>
                                    <Typography
                                        sx={{
                                            fontSize: 9,
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            color: GOLD,
                                            mt: 0.5,
                                            textAlign: 'right',
                                            width: 128,
                                        }}>
                                        {dl.sub}
                                    </Typography>
                                </Box>
                                {(workspace.participant.status === 'in_progress' ||
                                    workspace.participant.status === 'approved') &&
                                    (
                                        (workspace.participant as any).delivery_extension_requests as any[] | undefined
                                    )?.filter((r: any) => r.status === 'pending' && !r.round_id).length === 0 &&
                                    onRequestExtension && (
                                        <Button
                                            size="small"
                                            variant="contained"
                                            onClick={onRequestExtension}
                                            sx={{
                                                fontSize: 10,
                                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                                textTransform: 'none',
                                                borderRadius: 999,
                                                px: 2,
                                                py: 0.75,
                                                bgcolor: GOLD,
                                                color: '#131517',
                                                boxShadow: 'none',
                                                flexShrink: 0,
                                                '&:hover': { bgcolor: GOLD, boxShadow: 'none' },
                                            }}>
                                            Request extension
                                        </Button>
                                    )}
                                {(
                                    (workspace.participant as any).delivery_extension_requests as any[] | undefined
                                )?.some((r: any) => r.status === 'pending' && !r.round_id) && (
                                    <Typography
                                        sx={{
                                            fontSize: 9,
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            color: MINT,
                                            flexShrink: 0,
                                        }}>
                                        Extension pending review
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    ) : (
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: {
                                    xs: 'repeat(2, minmax(0,1fr))',
                                    sm: 'repeat(3, auto)',
                                    lg: 'auto auto auto',
                                },
                                alignItems: 'center',
                                gap: { xs: 2, sm: 3, lg: 6 },
                                columnGap: { lg: 8 },
                            }}>
                            <Box>
                                <Typography
                                    sx={{ fontSize: 10, color: ON_VARIANT, textTransform: 'uppercase', mb: 0.5 }}>
                                    Reward
                                </Typography>
                                <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 20 }}>{reward}</Typography>
                                {perDeliveryReward && (
                                    <Typography
                                        sx={{
                                            fontSize: 11,
                                            color: ON_VARIANT,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            mt: 0.25,
                                        }}>
                                        {perDeliveryReward} / delivery
                                    </Typography>
                                )}
                            </Box>
                            <Box>
                                <Typography
                                    sx={{ fontSize: 10, color: ON_VARIANT, textTransform: 'uppercase', mb: 0.5 }}>
                                    Status
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: 14,
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        color: st.dot,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                    }}>
                                    <Box
                                        sx={{
                                            width: 8,
                                            height: 8,
                                            borderRadius: '50%',
                                            bgcolor: st.dot,
                                            boxShadow: st.dot === MINT ? '0 0 10px #00fdc1' : undefined,
                                        }}
                                    />
                                    {st.label}
                                </Typography>
                            </Box>
                            <Box sx={{ gridColumn: { xs: '1 / -1', sm: 'auto' } }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'flex-end',
                                        mb: 0.5,
                                    }}>
                                    <Typography sx={{ fontSize: 10, color: ON_VARIANT, textTransform: 'uppercase' }}>
                                        Deadline
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: 10,
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            color: GOLD,
                                            ml: 2,
                                        }}>
                                        {dl.dateLine}
                                    </Typography>
                                </Box>
                                <Box
                                    sx={{
                                        width: 128,
                                        height: 6,
                                        bgcolor: SURFACE_HI,
                                        borderRadius: 999,
                                        overflow: 'hidden',
                                    }}>
                                    <Box
                                        sx={{
                                            height: '100%',
                                            width: `${dl.pct}%`,
                                            bgcolor: MINT,
                                            borderRadius: 999,
                                            boxShadow: '0 0 8px #00fdc1',
                                        }}
                                    />
                                </Box>
                                <Typography
                                    sx={{
                                        fontSize: 9,
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        color: GOLD,
                                        mt: 0.5,
                                        textAlign: 'right',
                                        width: 128,
                                    }}>
                                    {dl.sub}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </Box>
                {(isCircuul || isCommission) && (
                    <Box sx={{ mt: 2.5 }}>
                        <CreatorWorkspacePerformanceBoxes
                            circuulStats={workspace.circuul_stats}
                            commissionStats={workspace.commission_stats}
                            circuul={workspace.circuul}
                            basePayCents={workspace.campaign.base_payment_per_creator_cents ?? null}
                        />
                    </Box>
                )}
                {isRecurrent && totalRounds > 1 && (
                    <Box sx={{ mt: 2.5, pt: 2, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography
                                sx={{
                                    fontSize: 10,
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.16em',
                                    color: ON_VARIANT,
                                }}>
                                Delivery progress
                            </Typography>
                            <Typography sx={{ fontSize: 11, fontFamily: FONT_ENUM.MAIN_BOLD, color: GOLD }}>
                                {currentRoundNumber} of {totalRounds}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
                            {allRounds.map((r, i) => {
                                const roundPayout = workspace.participant.round_payouts?.find(
                                    rp => rp.round_number === r.round_number,
                                );
                                const isPaid = Boolean(roundPayout?.initiated_at);
                                const isActive = i === activeRoundIndex;
                                const isPast = i < activeRoundIndex;
                                return (
                                    <Box
                                        key={r._id ?? i}
                                        sx={{
                                            flex: 1,
                                            height: 6,
                                            borderRadius: 999,
                                            bgcolor: isPaid
                                                ? MINT
                                                : isActive
                                                ? GOLD
                                                : isPast
                                                ? 'rgba(255,201,101,0.35)'
                                                : 'rgba(255,255,255,0.1)',
                                            boxShadow: isPaid
                                                ? '0 0 6px #00fdc1'
                                                : isActive
                                                ? '0 0 6px rgba(255,201,101,0.6)'
                                                : undefined,
                                            transition: 'background-color 0.3s',
                                        }}
                                    />
                                );
                            })}
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.75 }}>
                            <Typography sx={{ fontSize: 9, color: ON_VARIANT, fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                                Delivery {activeRound?.round_number ?? 1}
                                {activeRound?.label ? ` · ${activeRound.label}` : ''}
                            </Typography>
                            <Typography sx={{ fontSize: 9, color: ON_VARIANT, fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                                {workspace.participant.round_payouts?.filter(rp => rp.initiated_at).length ?? 0} paid
                            </Typography>
                        </Box>
                    </Box>
                )}
            </Box>
            {showActivity ? (
                <CommissionActivityModal
                    open={activityOpen}
                    onClose={() => setActivityOpen(false)}
                    enrollmentId={workspace.circuul?.enrollment_id || workspace.commission?.enrollment_id || undefined}
                    programId={workspace.commission?.program_id || undefined}
                    campaignId={workspace.campaign._id}
                />
            ) : null}
        </>
    );
}

function DeliverableAndUploadPanel({
    deliverableType,
    onDeliverableType,
    campaignDeliverables,
    deliverableConstraints,
    submittedTypes,
    revisionTypes,
    totalRequired,
    totalSubmitted,
    requiredCount,
    files,
    onRemoveFile,
    fileInputRef,
    onFilesPicked,
    onBrowse,
    onSubmit,
    submitting,
    uploadProgress,
    errorMessage,
    fullSubmitPath,
    onNavigateFullSubmit,
    participationBlocked = false,
    participationLockMessage,
    campaignPlatforms = [],
    multiUpload = null,
    slotIndex = 0,
    submittedSlots,
    onSlotChange,
}: {
    deliverableType: string;
    onDeliverableType: (t: string) => void;
    campaignDeliverables: {
        type: string;
        label: string;
        count: number;
        duration_range?: string;
        multiple_uploads?: { enabled: boolean; count: number; labels: string[] };
    }[];
    deliverableConstraints: DeliverableConstraints | null;

    submittedTypes: Set<string>;
    revisionTypes: Map<string, string>;
    totalRequired: number;
    totalSubmitted: number;
    requiredCount: number;
    files: File[];
    onRemoveFile: (index: number) => void;
    fileInputRef: React.RefObject<HTMLInputElement>;
    onFilesPicked: (list: FileList | null) => void;
    onBrowse: () => void;
    onSubmit: (platforms: string[]) => void;
    submitting: boolean;
    uploadProgress: number;
    errorMessage: string | null;
    fullSubmitPath: string;
    onNavigateFullSubmit: () => void;
    participationBlocked?: boolean;
    participationLockMessage?: string;
    campaignPlatforms?: string[];
    multiUpload?: { enabled: boolean; count: number; labels: string[] } | null;
    slotIndex?: number;
    submittedSlots?: Set<number>;
    onSlotChange?: (i: number) => void;
}) {
    const progressPct = totalRequired > 0 ? Math.min(100, (totalSubmitted / totalRequired) * 100) : 0;
    const fileLabel = `${requiredCount} file${requiredCount > 1 ? 's' : ''}`;
    const revisionFeedback = revisionTypes.get(deliverableType);
    // Hide upload panel when all deliverables are approved/submitted (nothing to upload or revise)
    const hasActionable = campaignDeliverables.some(d => !submittedTypes.has(d.type));

    // ── Platform selection ───────────────────────────────────────────────────
    const [selectedPlatforms, setSelectedPlatforms] = useState<Set<string>>(() => new Set(campaignPlatforms));
    useEffect(() => {
        setSelectedPlatforms(new Set(campaignPlatforms));
    }, [campaignPlatforms.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

    // Sync deliverable type when platform chips change
    useEffect(() => {
        if (campaignPlatforms.length === 0) return;
        const match = campaignDeliverables.find(d => {
            if (submittedTypes.has(d.type)) return false;
            const tp = platformsForDeliverableType(d.type);
            return tp ? tp.some(p => selectedPlatforms.has(p)) : false;
        });
        if (match && match.type !== deliverableType) onDeliverableType(match.type);
    }, [selectedPlatforms.size]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Per-file media validation ────────────────────────────────────────────
    const [fileWarnings, setFileWarnings] = useState<Record<number, string[]>>({});
    useEffect(() => {
        if (!files.length || !deliverableConstraints) {
            setFileWarnings({});
            return;
        }
        const { durationRange, requiresVertical, requiresHorizontal } = deliverableConstraints;
        const bounds = parseDurationRange(durationRange);
        let cancelled = false;
        (async () => {
            const next: Record<number, string[]> = {};
            await Promise.all(
                files.map(async (file, idx) => {
                    const warnings: string[] = [];
                    try {
                        const { duration, width, height } = await getVideoMeta(file);
                        if (bounds) {
                            const [min, max] = bounds;
                            if (duration < min || duration > max) {
                                warnings.push(
                                    `Duration ${Math.round(
                                        duration,
                                    )}s is outside the required range (${durationRangeLabel(durationRange)}).`,
                                );
                            }
                        }
                        if (requiresVertical && width > 0 && height > 0 && width >= height) {
                            warnings.push(
                                'This deliverable requires a vertical (9:16) video — height must be greater than width.',
                            );
                        }
                        if (requiresHorizontal && width > 0 && height > 0 && height >= width) {
                            warnings.push(
                                'This deliverable requires a horizontal (16:9) video — width must be greater than height.',
                            );
                        }
                    } catch {
                        // metadata unreadable — skip silently
                    }
                    if (warnings.length) next[idx] = warnings;
                }),
            );
            if (!cancelled) setFileWarnings(next);
        })();
        return () => {
            cancelled = true;
        };
    }, [files, deliverableConstraints]);

    const hasWarnings = Object.keys(fileWarnings).length > 0;
    const countReady = files.length === requiredCount;

    return (
        <>
            <input
                ref={fileInputRef}
                type="file"
                multiple
                hidden
                accept="video/*"
                onChange={e => {
                    onFilesPicked(e.target.files);
                    e.target.value = '';
                }}
            />

            <Box
                sx={{
                    bgcolor: SURFACE_LOW,
                    p: 3,
                    borderRadius: 2,
                    border: '1px solid rgba(255,255,255,0.05)',
                }}>
                {/* Header + overall progress */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 13,
                            color: GOLD,
                            letterSpacing: '0.12em',
                            textTransform: 'uppercase',
                        }}>
                        Deliverables
                    </Typography>
                    {totalRequired > 0 && (
                        <Typography
                            sx={{
                                fontSize: 11,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                color: totalSubmitted >= totalRequired ? MINT : ON_VARIANT,
                            }}>
                            {totalSubmitted}/{totalRequired} submitted
                        </Typography>
                    )}
                </Box>

                {/* Progress bar */}
                {totalRequired > 0 && (
                    <Box
                        sx={{
                            width: '100%',
                            height: 4,
                            bgcolor: SURFACE_HI,
                            borderRadius: 999,
                            overflow: 'hidden',
                            mb: 2.5,
                        }}>
                        <Box
                            sx={{
                                height: '100%',
                                width: `${progressPct}%`,
                                bgcolor: progressPct >= 100 ? MINT : GOLD,
                                borderRadius: 999,
                                transition: 'width 0.4s ease',
                                boxShadow: progressPct >= 100 ? '0 0 8px #00fdc1' : undefined,
                            }}
                        />
                    </Box>
                )}

                {/* Platform checkboxes — shown when campaign targets specific platforms */}
                {campaignPlatforms.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                        {campaignPlatforms.map(platform => (
                            <FormControlLabel
                                key={platform}
                                control={
                                    <Checkbox
                                        checked={selectedPlatforms.has(platform)}
                                        onChange={e =>
                                            setSelectedPlatforms(prev => {
                                                const next = new Set(prev);
                                                if (e.target.checked) next.add(platform);
                                                else next.delete(platform);
                                                return next;
                                            })
                                        }
                                        size="small"
                                        sx={{
                                            color: 'rgba(255,255,255,0.25)',
                                            '&.Mui-checked': { color: GOLD },
                                            p: 0.5,
                                        }}
                                    />
                                }
                                label={
                                    <Typography
                                        sx={{
                                            fontSize: 12,
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            color: selectedPlatforms.has(platform) ? GOLD : ON_VARIANT,
                                            textTransform: 'capitalize',
                                        }}>
                                        {platform}
                                    </Typography>
                                }
                                sx={{ mr: 1 }}
                            />
                        ))}
                    </Box>
                )}

                {/* Deliverable rows from campaign */}
                {campaignDeliverables.length > 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {campaignDeliverables.map(({ type, label, count }) => {
                            const Icon = iconForLabel(label);
                            const typePlatforms = platformsForDeliverableType(type);
                            const platformActive = typePlatforms
                                ? typePlatforms.some(p => selectedPlatforms.has(p))
                                : campaignPlatforms.length === 0;
                            const selected = campaignPlatforms.length > 0 ? platformActive : deliverableType === type;
                            const done = submittedTypes.has(type);
                            const needsRevision = revisionTypes.has(type);
                            return (
                                <Button
                                    key={type}
                                    type="button"
                                    disabled={done}
                                    onClick={() => !done && onDeliverableType(type)}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: 1.5,
                                        px: 1.5,
                                        py: 1.25,
                                        borderRadius: 2,
                                        textTransform: 'none',
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontSize: 12,
                                        border: done
                                            ? `1px solid rgba(0,253,193,0.2)`
                                            : needsRevision
                                            ? `1px solid rgba(255,107,107,0.4)`
                                            : selected
                                            ? `1px solid ${GOLD}`
                                            : '1px solid rgba(255,255,255,0.05)',
                                        bgcolor: done
                                            ? 'rgba(0,253,193,0.05)'
                                            : needsRevision
                                            ? 'rgba(255,107,107,0.06)'
                                            : selected
                                            ? 'rgba(255,201,101,0.08)'
                                            : SURFACE,
                                        color: done ? MINT : needsRevision ? '#ff6b6b' : selected ? GOLD : ON_VARIANT,
                                        opacity: done ? 0.7 : 1,
                                        '&:hover': done
                                            ? {}
                                            : {
                                                  borderColor: needsRevision
                                                      ? 'rgba(255,107,107,0.6)'
                                                      : 'rgba(255,201,101,0.3)',
                                                  bgcolor: needsRevision
                                                      ? 'rgba(255,107,107,0.1)'
                                                      : 'rgba(255,201,101,0.05)',
                                                  color: needsRevision ? '#ff6b6b' : GOLD,
                                              },
                                        '&.Mui-disabled': {
                                            border: `1px solid rgba(0,253,193,0.2)`,
                                            bgcolor: 'rgba(0,253,193,0.05)',
                                            color: MINT,
                                            opacity: 0.7,
                                        },
                                    }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                                        <Icon sx={{ fontSize: 18, flexShrink: 0 }} />
                                        <Typography
                                            sx={{
                                                fontSize: 12,
                                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                                color: 'inherit',
                                                textAlign: 'left',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                textDecoration: done ? 'line-through' : 'none',
                                            }}>
                                            {label}
                                        </Typography>
                                    </Box>
                                    <Box
                                        sx={{
                                            flexShrink: 0,
                                            px: 1,
                                            py: 0.25,
                                            borderRadius: 999,
                                            bgcolor: done
                                                ? 'rgba(0,253,193,0.15)'
                                                : needsRevision
                                                ? 'rgba(255,107,107,0.15)'
                                                : selected
                                                ? 'rgba(255,201,101,0.15)'
                                                : SURFACE_HI,
                                            fontSize: 10,
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            color: done
                                                ? MINT
                                                : needsRevision
                                                ? '#ff6b6b'
                                                : selected
                                                ? GOLD
                                                : ON_VARIANT,
                                        }}>
                                        {done ? '✓' : needsRevision ? '!' : `×${count}`}
                                    </Box>
                                </Button>
                            );
                        })}
                    </Box>
                ) : (
                    <Typography sx={{ fontSize: 12, color: ON_VARIANT }}>
                        No deliverables specified for this campaign.
                    </Typography>
                )}
            </Box>

            {/* Slot selection — only shown for multi-upload deliverables */}
            {multiUpload && onSlotChange && (
                <Box sx={{ bgcolor: SURFACE_LOW, p: 2.5, borderRadius: 2, border: '1px solid rgba(255,255,255,0.05)' }}>
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 11,
                            color: GOLD,
                            letterSpacing: '0.12em',
                            textTransform: 'uppercase',
                            mb: 1.5,
                        }}>
                        Select Version
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {multiUpload.labels.map((label, i) => {
                            const slotDone = submittedSlots?.has(i);
                            return (
                                <Button
                                    key={i}
                                    size="small"
                                    onClick={() => onSlotChange(i)}
                                    sx={{
                                        px: 2,
                                        py: 0.75,
                                        borderRadius: 999,
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontSize: 12,
                                        textTransform: 'none',
                                        ...(slotDone
                                            ? {
                                                  bgcolor: 'rgba(0,253,193,0.15)',
                                                  color: MINT,
                                                  border: '1px solid rgba(0,253,193,0.3)',
                                              }
                                            : slotIndex === i
                                            ? { bgcolor: GOLD, color: '#392700' }
                                            : { bgcolor: SURFACE_HI, color: ON_VARIANT }),
                                    }}>
                                    {slotDone ? `✓ ${label}` : label}
                                </Button>
                            );
                        })}
                    </Box>
                </Box>
            )}

            <Box
                sx={{
                    bgcolor: SURFACE_LOW,
                    p: 3,
                    borderRadius: 2,
                    border: '1px solid rgba(255,255,255,0.05)',
                    flex: 1,
                    display: hasActionable || participationBlocked ? 'flex' : 'none',
                    flexDirection: 'column',
                    minHeight: { lg: 320 },
                    opacity: participationBlocked ? 0.55 : 1,
                    pointerEvents: participationBlocked ? 'none' : 'auto',
                }}>
                {participationBlocked ? (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mb: 2,
                            p: 1.5,
                            borderRadius: 2,
                            bgcolor: 'rgba(255,255,255,0.04)',
                            border: '1px solid rgba(255,255,255,0.08)',
                        }}>
                        <LockOutlinedIcon sx={{ color: GOLD, fontSize: 20 }} />
                        <Typography sx={{ fontSize: 12, color: ON_VARIANT }}>
                            {participationLockMessage || 'Uploads are paused while the campaign is on hold.'}
                        </Typography>
                    </Box>
                ) : null}
                {/* Header: title + file count label */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 13,
                            color: GOLD,
                            letterSpacing: '0.12em',
                            textTransform: 'uppercase',
                        }}>
                        Upload Content
                    </Typography>
                    <Typography sx={{ fontSize: 11, fontFamily: FONT_ENUM.MAIN_BOLD, color: ON_VARIANT }}>
                        {fileLabel} required
                    </Typography>
                </Box>

                {/* Platform delivery indicator */}
                {campaignPlatforms.length > 0 && selectedPlatforms.size > 0 && (
                    <Box
                        sx={{
                            mb: 2,
                            px: 1.5,
                            py: 1,
                            borderRadius: 2,
                            bgcolor: `${GOLD}0d`,
                            border: `1px solid ${GOLD}30`,
                        }}>
                        <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', lineHeight: 1.5 }}>
                            Uploading for{' '}
                            <Typography
                                component="span"
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    color: GOLD,
                                    fontSize: 12,
                                    textTransform: 'capitalize',
                                }}>
                                {[...selectedPlatforms].join(' & ')}
                            </Typography>
                            {selectedPlatforms.size > 1 ? ' deliverables' : ' deliverable'}
                        </Typography>
                    </Box>
                )}

                {revisionFeedback !== undefined && (
                    <Box
                        sx={{
                            mb: 2,
                            p: 1.5,
                            borderRadius: 2,
                            border: '1px solid rgba(255,107,107,0.3)',
                            bgcolor: 'rgba(255,107,107,0.06)',
                        }}>
                        <Typography
                            sx={{
                                fontSize: 10,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                color: '#ff6b6b',
                                textTransform: 'uppercase',
                                letterSpacing: '0.1em',
                                mb: 0.5,
                            }}>
                            Revision requested
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', lineHeight: 1.5 }}>
                            {revisionFeedback ||
                                'The brand has requested changes to this deliverable. Please re-upload.'}
                        </Typography>
                    </Box>
                )}

                {/* Media requirements hint */}
                {deliverableConstraints &&
                    (deliverableConstraints.durationRange !== 'n/a' ||
                        deliverableConstraints.requiresVertical ||
                        deliverableConstraints.requiresHorizontal) && (
                        <Box
                            sx={{
                                mb: 2,
                                p: 1.5,
                                borderRadius: 2,
                                border: '1px solid rgba(255,201,101,0.2)',
                                bgcolor: 'rgba(255,201,101,0.05)',
                            }}>
                            <Typography
                                sx={{
                                    fontSize: 10,
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    color: GOLD,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.1em',
                                    mb: 0.5,
                                }}>
                                Content requirements
                            </Typography>
                            {deliverableConstraints.durationRange !== 'n/a' && (
                                <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', lineHeight: 1.6 }}>
                                    Duration: {durationRangeLabel(deliverableConstraints.durationRange)}
                                </Typography>
                            )}
                            {deliverableConstraints.requiresVertical && (
                                <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', lineHeight: 1.6 }}>
                                    Orientation: vertical (9:16)
                                </Typography>
                            )}
                            {deliverableConstraints.requiresHorizontal && (
                                <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', lineHeight: 1.6 }}>
                                    Orientation: horizontal (16:9)
                                </Typography>
                            )}
                        </Box>
                    )}

                {errorMessage ? (
                    <Alert severity="error" sx={{ mb: 2, py: 0 }}>
                        {errorMessage}
                    </Alert>
                ) : null}

                <Box
                    onDragOver={e => e.preventDefault()}
                    onDrop={e => {
                        e.preventDefault();
                        onFilesPicked(e.dataTransfer.files);
                    }}
                    onClick={files.length < requiredCount ? onBrowse : undefined}
                    sx={{
                        flex: 1,
                        border: `2px dashed ${countReady ? 'rgba(0,253,193,0.4)' : 'rgba(255,255,255,0.1)'}`,
                        borderRadius: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        py: files.length > 0 ? 3 : 6,
                        px: 3,
                        textAlign: 'center',
                        cursor: files.length < requiredCount ? 'pointer' : 'default',
                        transition: 'all 0.2s',
                        '&:hover':
                            files.length < requiredCount
                                ? {
                                      borderColor: 'rgba(255,201,101,0.4)',
                                      bgcolor: 'rgba(255,255,255,0.02)',
                                  }
                                : {},
                    }}>
                    {files.length === 0 ? (
                        <>
                            <Box
                                sx={{
                                    width: 64,
                                    height: 64,
                                    borderRadius: '50%',
                                    bgcolor: SURFACE_HI,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    mb: 2,
                                }}>
                                <CloudUploadOutlinedIcon sx={{ fontSize: 36, color: GOLD }} />
                            </Box>
                            <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 14, mb: 0.5 }}>
                                Drag and drop your files
                            </Typography>
                            <Typography sx={{ color: ON_VARIANT, fontSize: 11, maxWidth: 220 }}>
                                Add {fileLabel} · MP4, MOV, WEBM up to 500MB
                            </Typography>
                            <Button
                                type="button"
                                onClick={e => {
                                    e.stopPropagation();
                                    onBrowse();
                                }}
                                sx={{
                                    mt: 3,
                                    px: 3,
                                    py: 1.25,
                                    borderRadius: 999,
                                    border: `1px solid ${GOLD}`,
                                    color: GOLD,
                                    fontSize: 12,
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    textTransform: 'none',
                                    '&:hover': { bgcolor: GOLD, color: '#392700' },
                                }}>
                                Browse Files
                            </Button>
                        </>
                    ) : (
                        <Box sx={{ width: '100%' }}>
                            {/* Counter badge */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    mb: 1.5,
                                }}>
                                <Typography
                                    sx={{
                                        fontSize: 11,
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        color: countReady ? MINT : GOLD,
                                    }}>
                                    {files.length} / {requiredCount} files added
                                </Typography>
                                {files.length < requiredCount && (
                                    <Button
                                        type="button"
                                        onClick={e => {
                                            e.stopPropagation();
                                            onBrowse();
                                        }}
                                        sx={{
                                            px: 1.5,
                                            py: 0.5,
                                            borderRadius: 999,
                                            border: `1px solid ${GOLD}`,
                                            color: GOLD,
                                            fontSize: 11,
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            textTransform: 'none',
                                            '&:hover': { bgcolor: GOLD, color: '#392700' },
                                        }}>
                                        + Add more
                                    </Button>
                                )}
                            </Box>
                            {/* File list with remove */}
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                {files.map((f, i) => {
                                    const warns = fileWarnings[i] ?? [];
                                    return (
                                        <Box key={`${f.name}-${f.size}-${i}`} onClick={e => e.stopPropagation()}>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    px: 1.5,
                                                    py: 1,
                                                    bgcolor: warns.length ? 'rgba(255,107,107,0.08)' : SURFACE_HI,
                                                    borderRadius: warns.length ? '6px 6px 0 0' : 1.5,
                                                    border: warns.length ? '1px solid rgba(255,107,107,0.3)' : 'none',
                                                    borderBottom: warns.length ? 'none' : undefined,
                                                    gap: 1,
                                                }}>
                                                <Typography
                                                    sx={{
                                                        fontSize: 11,
                                                        color: warns.length ? '#ff9999' : ON_VARIANT,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                        flex: 1,
                                                    }}>
                                                    {f.name}
                                                </Typography>
                                                <Button
                                                    type="button"
                                                    onClick={() => {
                                                        onRemoveFile(i);
                                                        setFileWarnings(prev => {
                                                            const next = { ...prev };
                                                            delete next[i];
                                                            return next;
                                                        });
                                                    }}
                                                    sx={{
                                                        minWidth: 0,
                                                        p: 0.25,
                                                        color: ON_VARIANT,
                                                        fontSize: 14,
                                                        lineHeight: 1,
                                                        '&:hover': { color: '#ff6b6b' },
                                                    }}>
                                                    ✕
                                                </Button>
                                            </Box>
                                            {warns.length > 0 && (
                                                <Box
                                                    sx={{
                                                        px: 1.5,
                                                        py: 1,
                                                        bgcolor: 'rgba(255,107,107,0.06)',
                                                        border: '1px solid rgba(255,107,107,0.3)',
                                                        borderTop: 'none',
                                                        borderRadius: '0 0 6px 6px',
                                                    }}>
                                                    {warns.map((w, wi) => (
                                                        <Typography
                                                            key={wi}
                                                            sx={{ fontSize: 11, color: '#ff9999', lineHeight: 1.5 }}>
                                                            ⚠ {w}
                                                        </Typography>
                                                    ))}
                                                </Box>
                                            )}
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Box>
                    )}
                </Box>

                <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                    {hasWarnings && !submitting && (
                        <Box
                            sx={{
                                mb: 2,
                                p: 1.5,
                                borderRadius: 2,
                                border: '1px solid rgba(255,107,107,0.3)',
                                bgcolor: 'rgba(255,107,107,0.06)',
                            }}>
                            <Typography sx={{ fontSize: 12, color: '#ff9999', lineHeight: 1.5 }}>
                                One or more files don&apos;t meet the content requirements above. Please replace them
                                before submitting.
                            </Typography>
                        </Box>
                    )}
                    {submitting && uploadProgress > 0 && (
                        <Box sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography sx={{ fontSize: 11, color: ON_VARIANT }}>Uploading…</Typography>
                                <Typography sx={{ fontSize: 11, color: GOLD }}>{uploadProgress}%</Typography>
                            </Box>
                            <LinearProgress
                                variant="determinate"
                                value={uploadProgress}
                                sx={{
                                    borderRadius: 4,
                                    bgcolor: SURFACE_HI,
                                    '& .MuiLinearProgress-bar': { bgcolor: GOLD, borderRadius: 4 },
                                }}
                            />
                        </Box>
                    )}
                    <Button
                        fullWidth
                        disabled={submitting || !countReady || hasWarnings}
                        onClick={() => onSubmit([...selectedPlatforms])}
                        sx={{
                            py: 2,
                            borderRadius: 2,
                            bgcolor: countReady ? GOLD : SURFACE_HI,
                            color: countReady ? '#392700' : ON_VARIANT,
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 12,
                            letterSpacing: '0.2em',
                            textTransform: 'uppercase',
                            boxShadow: countReady ? '0 0 20px rgba(255,201,101,0.25)' : '0 12px 40px rgba(0,0,0,0.35)',
                            transition: 'all 0.2s',
                            '&:hover': countReady ? { bgcolor: '#feb700' } : {},
                            '&.Mui-disabled': {
                                bgcolor: SURFACE_HI,
                                color: ON_VARIANT,
                            },
                        }}>
                        {submitting ? (
                            <CircularProgress size={22} sx={{ color: 'inherit' }} />
                        ) : countReady ? (
                            'Submit for Approval'
                        ) : (
                            `Add ${requiredCount - files.length} more file${
                                requiredCount - files.length > 1 ? 's' : ''
                            }`
                        )}
                    </Button>
                    {fullSubmitPath ? (
                        <Button
                            fullWidth
                            sx={{ mt: 1.5, color: ON_VARIANT, fontSize: 12, textTransform: 'none' }}
                            onClick={onNavigateFullSubmit}>
                            Open full submit form (notes &amp; link)
                        </Button>
                    ) : null}
                </Box>
            </Box>
        </>
    );
}

// ── Media constraint helpers ─────────────────────────────────────────────────

interface DeliverableConstraints {
    durationRange: string; // e.g. "15-30s", "n/a"
    requiresVertical: boolean; // type contains "short" or type === "vertical"
    requiresHorizontal: boolean; // type === "horizontal"
}

/** Parse a duration_range id into [minSec, maxSec] (Infinity = no upper bound). */
function parseDurationRange(range: string): [number, number] | null {
    if (!range || range === 'n/a') return null;
    if (range === '5min+') return [300, Infinity];
    const toSec = (n: number, unit: string) => (unit === 'min' ? n * 60 : n);
    // "30s-1min", "1min-3min" — each part has its own unit
    const twoUnits = range.match(/^(\d+)(s|min)-(\d+)(s|min)$/);
    if (twoUnits) {
        return [toSec(parseInt(twoUnits[1], 10), twoUnits[2]), toSec(parseInt(twoUnits[3], 10), twoUnits[4])];
    }
    // "0-15s", "15-30s" — shared unit at end applies to both numbers
    const sharedUnit = range.match(/^(\d+)-(\d+)(s|min)$/);
    if (sharedUnit) {
        return [toSec(parseInt(sharedUnit[1], 10), sharedUnit[3]), toSec(parseInt(sharedUnit[2], 10), sharedUnit[3])];
    }
    return null;
}

/** Human-readable label for a duration_range id. */
function durationRangeLabel(range: string): string {
    const map: Record<string, string> = {
        'n/a': 'any duration',
        '0-15s': 'up to 15 seconds',
        '15-30s': '15 – 30 seconds',
        '30s-1min': '30 seconds – 1 minute',
        '1min-3min': '1 – 3 minutes',
        '3min-5min': '3 – 5 minutes',
        '5min+': '5 minutes or more',
    };
    return map[range] ?? range;
}

async function getVideoMeta(file: File): Promise<{ duration: number; width: number; height: number }> {
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const video = document.createElement('video');
        video.preload = 'metadata';
        video.onloadedmetadata = () => {
            const result = { duration: video.duration, width: video.videoWidth, height: video.videoHeight };
            URL.revokeObjectURL(url);
            resolve(result);
        };
        video.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error('Could not read video metadata'));
        };
        video.src = url;
    });
}

function platformsForDeliverableType(type: string): ('instagram' | 'tiktok' | 'youtube')[] | undefined {
    if (type.startsWith('instagram_')) return ['instagram'];
    if (type.startsWith('tiktok_')) return ['tiktok'];
    if (type.startsWith('youtube_')) return ['youtube'];
    return undefined; // ugc_ or unknown → show all
}

function approvedSubmissionsForRound(
    submissions: IWorkspaceContentSubmission[],
    round: { _id?: string; round_number?: number } | null,
) {
    const subs = round?._id
        ? submissions.filter(
              s =>
                  String((s as { deliverable_round_id?: string }).deliverable_round_id || '') === String(round._id) ||
                  (s as { round_number?: number }).round_number === round.round_number,
          )
        : submissions;
    const map = new Map<string, IWorkspaceContentSubmission>();
    subs.filter(s => s.status === 'approved' && s.deliverable_type).forEach(s => map.set(s.deliverable_type!, s));
    return map;
}

function formatLiveLinkWindow(iso: string) {
    return new Date(iso).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });
}

function GoLivePanel({
    approvedSubmissions,
    activeType,
    onTypeChange,
    liveUrlInputs,
    onLiveUrlChange,
    onSubmit,
    submitting,
    onPublishSuccess,
    participationBlocked = false,
    participationLockMessage,
    isDirectHire = false,
    syncPublishDate,
    windowNote,
}: {
    approvedSubmissions: Map<string, IWorkspaceContentSubmission>;
    activeType: string;
    onTypeChange: (t: string) => void;
    liveUrlInputs: string[];
    onLiveUrlChange: (idx: number, val: string) => void;
    onSubmit: () => void;
    submitting: boolean;
    onPublishSuccess?: (platformPostUrl: string) => void;
    participationBlocked?: boolean;
    participationLockMessage?: string;
    isDirectHire?: boolean;
    syncPublishDate?: string;
    windowNote?: string;
}) {
    const [publishModalOpen, setPublishModalOpen] = useState(false);
    const [publishLockVersion, setPublishLockVersion] = useState(0);
    const { requireUploadEntitlement } = usePlanEntitlementGates();
    const types = Array.from(approvedSubmissions.keys());
    const active = approvedSubmissions.get(activeType);
    const required = active?.expected_count ?? 1;
    const alreadySubmitted = Boolean(active?.live_urls?.length);
    const socialPublishLocked = Boolean(active?._id && isSocialPublishLocked(active._id));

    useEffect(() => {
        if (!active?._id || !isSocialPublishLocked(active._id)) return;
        const id = window.setInterval(() => setPublishLockVersion(v => v + 1), 15_000);
        return () => clearInterval(id);
    }, [active?._id, publishLockVersion]);
    const filled = Array.from({ length: required }, (_, i) => (liveUrlInputs[i] ?? '').trim());
    const allValid = filled.every(u => u.startsWith('http'));

    const thumbnail = active?.projects?.[0]?.feature_image ?? active?.projects?.[0]?.thumbnail;
    const approvedAt = active?.approved_at
        ? new Date(active.approved_at).toLocaleDateString(undefined, {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
          })
        : null;

    return (
        <Box
            sx={{
                bgcolor: SURFACE_LOW,
                p: 3,
                borderRadius: 2,
                border: '1px solid rgba(0,253,193,0.15)',
                boxShadow: 'inset 0 0 20px rgba(0,253,193,0.03)',
                opacity: participationBlocked ? 0.55 : 1,
                pointerEvents: participationBlocked ? 'none' : 'auto',
            }}>
            {/* Header */}
            <Typography
                sx={{
                    fontFamily: FONT_ENUM.MAIN_BOLD,
                    fontSize: 13,
                    color: MINT,
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                    mb: 2,
                }}>
                Approved work
            </Typography>
            {windowNote ? (
                <Typography sx={{ fontSize: 12, color: GOLD, mb: 2, lineHeight: 1.5 }}>{windowNote}</Typography>
            ) : null}
            {participationBlocked ? (
                <Typography sx={{ fontSize: 12, color: ON_VARIANT, mb: 2 }}>
                    {participationLockMessage ||
                        'Marking as shared and submitting live URLs are paused while the campaign is on hold.'}
                </Typography>
            ) : null}

            {types.length > 1 && (
                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                    {types.map(t => (
                        <Button
                            key={t}
                            onClick={() => onTypeChange(t)}
                            sx={{
                                px: 1.5,
                                py: 0.5,
                                borderRadius: 999,
                                textTransform: 'none',
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 11,
                                border: activeType === t ? `1px solid ${MINT}` : '1px solid rgba(255,255,255,0.1)',
                                color: activeType === t ? MINT : ON_VARIANT,
                                bgcolor: activeType === t ? 'rgba(0,253,193,0.08)' : 'transparent',
                            }}>
                            {DELIVERABLE_TYPE_LABELS[t] ?? t}
                        </Button>
                    ))}
                </Box>
            )}

            {/* Submission identity card */}
            {active && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        bgcolor: SURFACE,
                        borderRadius: 2,
                        p: 1.5,
                        mb: 2,
                        border: '1px solid rgba(0,253,193,0.12)',
                    }}>
                    {thumbnail ? (
                        <Box
                            component="img"
                            src={thumbnail}
                            alt={activeType}
                            sx={{ width: 52, height: 52, borderRadius: 1.5, objectFit: 'cover', flexShrink: 0 }}
                        />
                    ) : (
                        <Box
                            sx={{
                                width: 52,
                                height: 52,
                                borderRadius: 1.5,
                                bgcolor: SURFACE_HI,
                                flexShrink: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}>
                            <VideocamOutlinedIcon sx={{ color: ON_VARIANT, fontSize: 22 }} />
                        </Box>
                    )}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 13, color: '#fff' }} noWrap>
                            {DELIVERABLE_TYPE_LABELS[activeType] ?? activeType}
                        </Typography>
                        {approvedAt && (
                            <Typography sx={{ fontSize: 11, color: ON_VARIANT, mt: 0.25 }}>
                                Approved {approvedAt}
                            </Typography>
                        )}
                    </Box>
                    <Box sx={{ px: 1.25, py: 0.4, borderRadius: 999, bgcolor: 'rgba(0,253,193,0.1)', flexShrink: 0 }}>
                        <Typography sx={{ fontSize: 10, fontFamily: FONT_ENUM.MAIN_BOLD, color: MINT }}>
                            Approved
                        </Typography>
                    </Box>
                </Box>
            )}

            {/* Publish to Social — primary CTA (hidden for direct hire) */}
            {!isDirectHire && (
                <>
                    <Button
                        fullWidth
                        onClick={() => {
                            if (!requireUploadEntitlement()) return;
                            setPublishModalOpen(true);
                        }}
                        disabled={!active?._id || socialPublishLocked}
                        sx={{
                            py: 1.5,
                            borderRadius: 2,
                            textTransform: 'none',
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 13,
                            mb: socialPublishLocked ? 1 : 2,
                            background:
                                active?._id && !socialPublishLocked
                                    ? `linear-gradient(90deg, ${GOLD}, #feb700)`
                                    : 'rgba(255,255,255,0.06)',
                            color: active?._id && !socialPublishLocked ? '#392700' : ON_VARIANT,
                            boxShadow:
                                active?._id && !socialPublishLocked ? '0 4px 16px rgba(255,201,101,0.2)' : 'none',
                            '&:hover':
                                active?._id && !socialPublishLocked
                                    ? { background: `linear-gradient(90deg, #ffd080, #feb700)` }
                                    : {},
                            '&.Mui-disabled': { background: 'rgba(255,255,255,0.06)', color: ON_VARIANT },
                        }}>
                        Publish to Social
                    </Button>
                    {socialPublishLocked && (
                        <Typography sx={{ fontSize: 12, color: ON_VARIANT, lineHeight: 1.5, mb: 2 }}>
                            A social publish is already in progress for this submission. Please wait a few minutes
                            before publishing again.
                        </Typography>
                    )}
                </>
            )}

            <PublishToSocialModal
                open={publishModalOpen}
                onClose={() => setPublishModalOpen(false)}
                submissionId={active?._id ?? ''}
                submissionLabel={DELIVERABLE_TYPE_LABELS[activeType] ?? activeType}
                thumbnail={thumbnail}
                allowedPlatforms={platformsForDeliverableType(activeType)}
                onPublishActivity={() => setPublishLockVersion(v => v + 1)}
                onPublishSuccess={onPublishSuccess}
                syncPublishDate={syncPublishDate}
            />

            {!isDirectHire && (
                <>
                    {/* Divider with label */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                        <Box sx={{ flex: 1, height: '1px', bgcolor: 'rgba(255,255,255,0.07)' }} />
                        <Typography
                            sx={{
                                fontSize: 10,
                                color: ON_VARIANT,
                                textTransform: 'uppercase',
                                letterSpacing: '0.1em',
                            }}>
                            or submit live URL manually
                        </Typography>
                        <Box sx={{ flex: 1, height: '1px', bgcolor: 'rgba(255,255,255,0.07)' }} />
                    </Box>

                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 13,
                            color: MINT,
                            letterSpacing: '0.12em',
                            textTransform: 'uppercase',
                            mb: 2,
                        }}>
                        Submit Live {required > 1 ? 'URLs' : 'URL'}
                    </Typography>

                    {alreadySubmitted ? (
                        <Box
                            sx={{
                                p: 1.5,
                                borderRadius: 2,
                                border: '1px solid rgba(0,253,193,0.2)',
                                bgcolor: 'rgba(0,253,193,0.04)',
                            }}>
                            <Typography
                                sx={{
                                    fontSize: 10,
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    color: MINT,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.1em',
                                    mb: 1,
                                }}>
                                Live {(active?.live_urls?.length ?? 0) > 1 ? 'URLs' : 'URL'} submitted
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                {(active?.live_urls ?? []).map((url, i) => (
                                    <Typography
                                        key={i}
                                        sx={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', wordBreak: 'break-all' }}>
                                        {url}
                                    </Typography>
                                ))}
                            </Box>
                            <Typography sx={{ fontSize: 11, color: ON_VARIANT, mt: 1 }}>
                                Waiting for the brand to confirm.
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            <Typography sx={{ fontSize: 12, color: ON_VARIANT, mb: 1.5, lineHeight: 1.5 }}>
                                Your content has been approved. Paste{' '}
                                {required > 1 ? `${required} live post links` : 'the link to your live social post'}{' '}
                                below.
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
                                {Array.from({ length: required }, (_, i) => {
                                    const val = liveUrlInputs[i] ?? '';
                                    const valid = val.trim().startsWith('http');
                                    return (
                                        <Box key={i}>
                                            {required > 1 && (
                                                <Typography
                                                    sx={{
                                                        fontSize: 10,
                                                        color: ON_VARIANT,
                                                        mb: 0.5,
                                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                                        textTransform: 'uppercase',
                                                        letterSpacing: '0.08em',
                                                    }}>
                                                    Link {i + 1}
                                                </Typography>
                                            )}
                                            <Box
                                                component="input"
                                                value={val}
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                    onLiveUrlChange(i, e.target.value)
                                                }
                                                placeholder="https://instagram.com/p/..."
                                                sx={{
                                                    width: '100%',
                                                    px: 1.5,
                                                    py: 1.25,
                                                    borderRadius: 1.5,
                                                    border: `1px solid ${
                                                        valid ? 'rgba(0,253,193,0.4)' : 'rgba(255,255,255,0.1)'
                                                    }`,
                                                    bgcolor: SURFACE,
                                                    color: '#fff',
                                                    fontSize: 12,
                                                    outline: 'none',
                                                    boxSizing: 'border-box',
                                                    '&::placeholder': { color: ON_VARIANT },
                                                    transition: 'border-color 0.2s',
                                                }}
                                            />
                                        </Box>
                                    );
                                })}
                            </Box>
                            <Button
                                fullWidth
                                disabled={submitting || !allValid}
                                onClick={onSubmit}
                                sx={{
                                    py: 1.5,
                                    borderRadius: 2,
                                    textTransform: 'uppercase',
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontSize: 12,
                                    letterSpacing: '0.15em',
                                    bgcolor: allValid ? MINT : SURFACE_HI,
                                    color: allValid ? '#0a0a0a' : ON_VARIANT,
                                    boxShadow: allValid ? '0 0 16px rgba(0,253,193,0.25)' : 'none',
                                    transition: 'all 0.2s',
                                    '&:hover': allValid ? { bgcolor: '#00e0ab' } : {},
                                    '&.Mui-disabled': { bgcolor: SURFACE_HI, color: ON_VARIANT },
                                }}>
                                {submitting ? (
                                    <CircularProgress size={20} sx={{ color: 'inherit' }} />
                                ) : (
                                    `Submit Live ${required > 1 ? 'URLs' : 'URL'}`
                                )}
                            </Button>
                        </>
                    )}
                </>
            )}
        </Box>
    );
}

function ChatChrome({ workspace }: { workspace: ICampaignWorkspaceData }) {
    const mgr = brandManager(workspace);
    return (
        <Box
            sx={{
                px: 2,
                py: 2,
                bgcolor: SURFACE,
                borderBottom: '1px solid rgba(255,255,255,0.05)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
            }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                <Box sx={{ position: 'relative', flexShrink: 0 }}>
                    <Box
                        sx={{
                            width: 36,
                            height: 36,
                            borderRadius: '50%',
                            overflow: 'hidden',
                            border: '1px solid rgba(255,201,101,0.2)',
                            bgcolor: SURFACE_HI,
                        }}>
                        {mgr.avatar ? (
                            <CustomLazyLoadImage
                                src={mgr.avatar}
                                alt=""
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                        ) : (
                            <Typography sx={{ p: 0.75, fontSize: 13, color: ON_VARIANT, textAlign: 'center' }}>
                                {mgr.name.charAt(0)}
                            </Typography>
                        )}
                    </Box>
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: 0,
                            right: 0,
                            width: 10,
                            height: 10,
                            bgcolor: MINT,
                            border: `2px solid ${SURFACE}`,
                            borderRadius: '50%',
                        }}
                    />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 14 }} noWrap>
                        {mgr.name}
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: 8,
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            letterSpacing: '0.15em',
                            textTransform: 'uppercase',
                            color: GOLD,
                        }}>
                        {mgr.subtitle}
                    </Typography>
                </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5, color: ON_VARIANT, alignItems: 'center' }}>
                <LockOutlinedIcon
                    aria-label="Video call unavailable"
                    sx={{
                        fontSize: 17,
                        color: 'rgba(255,255,255,0.28)',
                        cursor: 'default',
                        pointerEvents: 'none',
                    }}
                />
                <MoreVertIcon sx={{ fontSize: 24, cursor: 'pointer', '&:hover': { color: GOLD } }} />
            </Box>
        </Box>
    );
}
